import React, { useContext, useEffect, useRef, useState } from "react";
import { Nav, Modal, Spinner, Button, Form } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Box, Paper, Tooltip } from "@mui/material";
import moment from 'moment';
import { approveOrder, getHomepageLiveOrders, rejectOrder } from "../api/context/orders";
import Loader from "../components/loader/loader";
import ordernew from '../assets/images/order-new.png';
import orderexchange from '../assets/images/order-exchange.png';
import orderreturn from '../assets/images/order-return.png';
import toast from 'react-hot-toast';
import { getHomepageCmsData } from "../api/context/dashboard";
import { useNavigate } from "react-router-dom";
import { SelectionContext } from "../Context/Store";
import dayjs from "dayjs";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useRecoilState } from "recoil";
import { orderListFilter } from "../atoms/orderListFilter";
import { FiRefreshCw, FiTruck, FiAlertTriangle, FiLayers, FiCalendar, FiX, FiTag, FiGrid, FiFolder, FiImage, FiPlayCircle } from "react-icons/fi";
import { FaRupeeSign, FaStore } from "react-icons/fa";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const queueDatatableCustomStyles = {
  headRow: {
    style: {
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      minHeight: '48px',
    },
  },
  headCells: {
    style: {
      display: 'flex',
      justifyContent: 'center',
      fontWeight: '600',
      fontSize: '9px',
      padding: '10px 6px',
      color: '#475569',
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      backgroundColor: '#f8fafc',
    },
  },
  cells: {
    style: {
      display: 'flex',
      fontSize: '10px',
      fontWeight: '500',
      color: '#0f172a',
      padding: '10px 6px',
    },
  },
  rows: {
    style: {
      minHeight: '48px',
      borderBottom: '1px solid #eef2f7',
      transition: 'background 0.15s ease',
      '&:not(:last-of-type)': {
        borderBottom: '1px solid #eef2f7',
      },
      '&:hover': {
        backgroundColor: '#f8fafc !important',
        cursor: 'pointer',
      },
    },
  },
  pagination: {
    style: {
      borderTop: '1px solid #eef2f7',
      minHeight: '52px',
      fontSize: '12px',
      color: '#475569',
    },
  },
};

const statusChipStyle = (bg, color = '#fff') => ({
  display: 'inline-block',
  backgroundColor: bg,
  color,
  padding: '3px 8px',
  borderRadius: '4px',
  fontWeight: 600,
  fontSize: '10px',
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
});

const STATUS_META = {
  INITIATED: { label: "Pending", color: "#f59e0b" },
  CONFIRMED: { label: "Confirmed", color: "#3b82f6" },
  DELIVERED: { label: "Delivered", color: "#10b981" },
  REJECTED: { label: "Rejected", color: "#ef4444" },
  CANCELLED: { label: "Cancelled", color: "#94a3b8" },
};

const HOME_FONT = {
  greeting:     { fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.25, letterSpacing: '-0.01em' },
  subheading:   { fontSize: '12px', color: '#94a3b8', fontWeight: 500, margin: '4px 0 0' },
  cardLabel:    { fontSize: '12px', fontWeight: 500, color: '#64748b' },
  cardValue:    { fontSize: '24px', fontWeight: 800, color: '#0f172a', lineHeight: 1.05, letterSpacing: '-0.035em', fontFamily: '"Sora", ui-sans-serif, system-ui, sans-serif', fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum"' },
  cardFootLbl:  { fontSize: '10px', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' },
  cardFootVal:  { fontSize: '11px', fontWeight: 600, color: '#0f172a' },
  panelTitle:   { fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: 0 },
  panelSubtle:  { fontSize: '11px', color: '#94a3b8', fontWeight: 500, margin: 0, marginTop: '2px' },
  filterCtrl:   { fontSize: '12px' },
  tabText:      { fontSize: '12px', fontWeight: 600 },
  tableHeader:  { fontSize: '11px', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em' },
  tableCell:    { fontSize: '12px', fontWeight: 500, color: '#0f172a' },
};

const formatINR = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const STAT_CYCLE_MS = 6000;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatDdMmYy = (iso) => {
  if (!iso || typeof iso !== 'string' || iso.length < 10) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  const monthIdx = Number(m) - 1;
  const monthName = MONTH_NAMES[monthIdx] || m;
  return `${Number(d)} ${monthName} ${y}`;
};
const StatCard = ({ icon: Icon, label, value, accent, footLabel, footValue, footOptions, autoCycle = true, onNavigate, cycleMs, footSwapVariant }) => {
  const [footIdx, setFootIdx] = useState(0);
  const hasOptions = Array.isArray(footOptions) && footOptions.length > 1;
  const foot = hasOptions
    ? footOptions[footIdx % footOptions.length]
    : (Array.isArray(footOptions) ? footOptions[0] : { label: footLabel, value: footValue });
  const cycling = hasOptions && autoCycle;
  const activeCycleMs = cycleMs || STAT_CYCLE_MS;
  useEffect(() => {
    if (!cycling) return;
    const t = setInterval(() => {
      setFootIdx((i) => (i + 1) % footOptions.length);
    }, activeCycleMs);
    return () => clearInterval(t);
  }, [cycling, footOptions?.length, activeCycleMs]);
  const handleFootClick = (e) => {
    if (!hasOptions) return;
    e.stopPropagation();
    setFootIdx((i) => (i + 1) % footOptions.length);
  };
  const activeColor = (foot && foot.color) || '#008695';
  const valueStyle = (foot && foot.color)
    ? { ...HOME_FONT.cardFootVal, color: foot.color }
    : HOME_FONT.cardFootVal;
  const labelStyle = (foot && foot.color)
    ? { ...HOME_FONT.cardFootLbl, color: foot.color }
    : HOME_FONT.cardFootLbl;
  const isClickable = typeof onNavigate === 'function';
  return (
    <div
      className={`modern-stat-card${isClickable ? ' modern-stat-card-clickable' : ''}`}
      onClick={isClickable ? onNavigate : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(); } } : undefined}
    >
      <div className="stat-top-row">
        <div style={HOME_FONT.cardLabel}>{label}</div>
        <div className="stat-icon-wrap" style={{ background: accent.bg, color: accent.fg }}>
          <Icon size={14} strokeWidth={2} />
        </div>
      </div>
      <div style={HOME_FONT.cardValue}>{value}</div>
      {(foot && (foot.label || foot.value !== undefined)) ? (
        <div
          className={`stat-foot${hasOptions ? ' stat-foot-cycle' : ''}`}
          onClick={handleFootClick}
          role={hasOptions ? 'button' : undefined}
          tabIndex={hasOptions ? 0 : undefined}
          onKeyDown={hasOptions ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleFootClick(e); } } : undefined}
          title={hasOptions ? 'Tap to switch' : undefined}
        >
          {cycling && footSwapVariant !== 'vscroll' && (
            <span
              key={footIdx}
              className="stat-foot-progress"
              style={{ background: activeColor, animationDuration: `${activeCycleMs}ms` }}
            />
          )}
          {footSwapVariant === 'vscroll' ? (
            <>
              <span className="stat-foot-vscroll-wrap">
                <span key={`l-${footIdx}`} className="stat-foot-vscroll-inner" style={labelStyle}>
                  {foot.label}
                </span>
              </span>
              <span className="stat-foot-vscroll-wrap">
                <span key={`v-${footIdx}`} className="stat-foot-vscroll-inner" style={valueStyle}>
                  {foot.value}
                </span>
              </span>
            </>
          ) : (
            <>
              <span style={labelStyle}>{foot.label}</span>
              <span style={valueStyle}>{foot.value}</span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export const Dashboard = () => {
  const currentUser = JSON.parse(localStorage.getItem('user')) || {};
  const userType = currentUser?.userType;
  const navigate = useNavigate();

  const greetingName = userType === 'ADMIN'
    ? 'Admin'
    : (currentUser?.firstName || currentUser?.storeName || 'there');

  const greetingPrefix = (() => {
    const h = new Date().getHours();
    if (h < 5)  return 'Good Night';       // 12 AM – 4:59 AM
    if (h < 12) return 'Good Morning';     // 5 AM – 11:59 AM
    if (h < 17) return 'Good Afternoon';   // 12 PM – 4:59 PM
    if (h < 21) return 'Good Evening';     // 5 PM – 8:59 PM
    return 'Good Night';                   // 9 PM – 11:59 PM
  })();

  const greetingSubtitle = userType === 'ADMIN'
    ? 'Platform sales, orders & inventory'
    : "Here's your store performance overview";

  const { ordersTrigger, setOrdersTrigger } = useContext(SelectionContext);
  const [filterData] = useRecoilState(orderListFilter);

  const [orders, setOrders] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderActionLoadingId, setOrderActionLoadingId] = useState(null);
  const [orderActionLoadingType, setOrderActionLoadingType] = useState(null);
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [pageactive, setPageActive] = useState(1);
  const [pageinactive, setPageInactive] = useState(1);
  const [limitactive] = useState(10);
  const [limitinactive] = useState(10);
  const [refreshing, setRefreshing] = useState(false);
  const [update, setUpdate] = useState(false);
  const [updateDashboard] = useState(false);
  const [initial, setInitial] = useState(false);
  const [search] = useState("");
  const [filter, setFilter] = useState("all");
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('ALL');
  const [presetOpen, setPresetOpen] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [pendingRejectOrder, setPendingRejectOrder] = useState(null);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [pendingApproveOrder, setPendingApproveOrder] = useState(null);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [evidenceOrder, setEvidenceOrder] = useState(null);
  const [evidenceVideoUrl, setEvidenceVideoUrl] = useState(null);
  const [evidenceReturnReason, setEvidenceReturnReason] = useState('');
  const [evidenceReturnComments, setEvidenceReturnComments] = useState('');
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [videoPreviewData, setVideoPreviewData] = useState({ videoUrl: null, reason: '', comments: '', orderId: '', orderType: null, row: null });
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceAction, setEvidenceAction] = useState(null); // 'REJECT' | 'APPROVE'
  const presetWrapRef = useRef(null);

  const EVIDENCE_REJECTION_REASONS = [
    "Product damaged by customer",
    "Wrong product returned",
    "Return period expired",
    "Missing original packaging",
    "Product used/worn",
  ];

  const [homepageData, setHomepageData] = useState({
    sales: {},
    revenue: {},
    products: {},
  });

  const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  };

  const [globalFilter, setGlobalFilter] = useState({
    timeRange: "4",
    ...getDefaultDates(),
  });

  // Shared preset definitions (used by both mobile bottom sheet + desktop dropdown)
  const HOMEPAGE_PRESETS = [
    { key: 'today',       label: 'Today',         days: 0 },
    { key: 'yesterday',   label: 'Yesterday',     days: 1, one: true },
    { key: 'last7',       label: 'Last 7 days',   days: 6 },
    { key: 'last15',      label: 'Last 15 days',  days: 14 },
    { key: 'last30',      label: 'Last 30 days',  days: 29 },
    { key: 'thisMonth',   label: 'This month',    thisMonth: true },
    { key: 'lastMonth',   label: 'Last month',    lastMonth: true },
    { key: 'last2m',      label: 'Last 2 months', months: 2 },
    { key: 'last3m',      label: 'Last 3 months', months: 3 },
    { key: 'last6m',      label: 'Last 6 months', months: 6 },
    { key: 'thisYear',    label: 'This year',     thisYear: true },
  ];
  const computePresetRange = (p) => {
    const today = new Date();
    let s, e;
    if (p.thisMonth) {
      s = new Date(today.getFullYear(), today.getMonth(), 1);
      e = today;
    } else if (p.lastMonth) {
      s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      e = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (p.thisYear) {
      s = new Date(today.getFullYear(), 0, 1);
      e = today;
    } else if (p.months) {
      s = new Date(today);
      s.setMonth(s.getMonth() - p.months);
      e = today;
    } else if (p.one) {
      s = new Date(today);
      s.setDate(s.getDate() - p.days);
      e = new Date(s);
    } else {
      s = new Date(today);
      s.setDate(s.getDate() - p.days);
      e = today;
    }
    const fmt = (d) => d.toISOString().split('T')[0];
    return { start: fmt(s), end: fmt(e) };
  };

  // Mobile-only date range bottom sheet
  const [showDateSheet, setShowDateSheet] = useState(false);
  const [sheetStart, setSheetStart] = useState(globalFilter.startDate);
  const [sheetEnd, setSheetEnd]     = useState(globalFilter.endDate);
  const openDateSheet = () => {
    setSheetStart(globalFilter.startDate);
    setSheetEnd(globalFilter.endDate);
    setShowDateSheet(true);
  };
  const applyDateSheet = () => {
    if (!sheetStart || !sheetEnd) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (sheetStart > sheetEnd) {
      toast.error('Start date cannot be after end date');
      return;
    }
    setGlobalFilter(prev => ({ ...prev, startDate: sheetStart, endDate: sheetEnd, timeRange: "4" }));
    applyGlobalFilter("4", sheetStart, sheetEnd);
    setShowDateSheet(false);
  };

  const salesStats = homepageData?.sales || {};
  const revenueStats = homepageData?.revenue || {};
  const productStats = homepageData?.products || {};
  const vendorStats = homepageData?.vendors || {};
  const brandStats = homepageData?.brands || {};
  const subSubCategoryStats = homepageData?.subSubCategories || {};
  const subCategoryStats = homepageData?.subCategories || {};

  const isOnlinePaymentPending = (order) => (
    order?.paymentMode === 'ONLINE' && order?.paymentStatus === 'PENDING'
  );

  const refreshLiveOrders = (silent = false) => {
    if (!silent) setOrdersLoading(true);
    getHomepageLiveOrders({ page, limit })((response) => {
      if (response?.statusCode === 200) {
        const data = response?.data || {};
        const list = Array.isArray(data) ? data : (data.orders || data.rows || []);
        setOrders(list);
        setTotalRows(data.totalItems ?? data.total ?? list.length);
      }
      if (!silent) setOrdersLoading(false);
    });
  };

  const handleLiveOrderStatus = (order, status) => {
    if (!order?.id || orderActionLoadingId) return;
    const isReject = status === 'REJECTED';

    if (isReject) {
      setPendingRejectOrder(order);
      setRejectionReason('');
      setCustomReason('');
      setShowRejectConfirm(true);
      return;
    }

    // Approve flow — show product-review modal first
    setPendingApproveOrder(order);
    setShowApproveConfirm(true);
  };

  const confirmApproveOrder = () => {
    if (!pendingApproveOrder?.id || orderActionLoadingId) return;

    setOrderActionLoadingId(pendingApproveOrder.id);
    setOrderActionLoadingType('ACCEPT');
    approveOrder({ orderIds: [pendingApproveOrder.id], status: 'CONFIRMED' })((response) => {
      if (response?.data?.statusCode === 200) {
        toast.success(response.data.message || 'Order accepted successfully');
        refreshLiveOrders(true);
      } else {
        toast.error(response?.data?.message || 'Something went wrong');
      }
      setOrderActionLoadingId(null);
      setOrderActionLoadingType(null);
      setPendingApproveOrder(null);
      setShowApproveConfirm(false);
    });
  };

  const confirmRejectOrder = () => {
    if (!pendingRejectOrder?.id || orderActionLoadingId) return;

    const needsReason = pendingRejectOrder?.orderType === 1 || pendingRejectOrder?.orderType === 2;
    const payload = { orderIds: [pendingRejectOrder.id] };

    if (needsReason) {
      let finalReason = rejectionReason;
      if (!rejectionReason && customReason && customReason.trim().length >= 10) {
        finalReason = customReason.trim();
      } else if (!rejectionReason) {
        toast.error('Please select a reason or provide a custom reason (min. 10 characters)');
        return;
      }
      payload.reason = finalReason;
    }

    setOrderActionLoadingId(pendingRejectOrder.id);
    setOrderActionLoadingType('REJECT');
    rejectOrder(payload)((response) => {
      if (response?.data?.statusCode === 200) {
        toast.success(response.data.message || 'Order rejected successfully');
        refreshLiveOrders(true);
      } else {
        toast.error(response?.data?.message || 'Something went wrong');
      }
      setOrderActionLoadingId(null);
      setOrderActionLoadingType(null);
      setPendingRejectOrder(null);
      setRejectionReason('');
      setCustomReason('');
      setShowRejectConfirm(false);
    });
  };

  const getEvidenceInfoFromOrder = (order) => {
    const pickFromProducts = (products) => {
      if (!Array.isArray(products)) return null;
      for (const p of products) {
        const returns = p?.returns || [];
        for (const r of returns) {
          const video = (r?.returnImages || []).find(img => img?.mediaType === 'video');
          if (video?.mediaUrl || r?.reason || r?.comments) {
            return { videoUrl: video?.mediaUrl || null, reason: r?.reason || '', comments: r?.comments || '' };
          }
        }
      }
      return null;
    };
    return pickFromProducts(order?.orderProducts) || pickFromProducts(order?.baseOrder?.orderProducts) || { videoUrl: null, reason: '', comments: '' };
  };

  // Backwards-compat wrapper (still used elsewhere if needed)
  const getEvidenceVideoFromOrder = (order) => getEvidenceInfoFromOrder(order)?.videoUrl || null;

  const openEvidenceModal = (row) => {
    setEvidenceOrder(row);
    const info = getEvidenceInfoFromOrder(row);
    setEvidenceVideoUrl(info?.videoUrl || null);
    setEvidenceReturnReason(info?.reason || '');
    setEvidenceReturnComments(info?.comments || '');
    setRejectionReason('');
    setCustomReason('');
    setShowEvidenceModal(true);
  };

  const openVideoPreview = (row) => {
    const info = getEvidenceInfoFromOrder(row);
    setVideoPreviewData({
      videoUrl: info?.videoUrl || null,
      reason: info?.reason || '',
      comments: info?.comments || '',
      orderId: row?.orderId || '',
      orderType: row?.orderType || null,
      row,
    });
    setShowVideoPreview(true);
  };

  const closeVideoPreview = () => {
    setShowVideoPreview(false);
    setVideoPreviewData({ videoUrl: null, reason: '', comments: '', orderId: '', orderType: null, row: null });
  };

  const handleVideoPreviewAccept = () => {
    const row = videoPreviewData?.row;
    if (!row) return;
    setShowVideoPreview(false);
    setVideoPreviewData({ videoUrl: null, reason: '', comments: '', orderId: '', orderType: null, row: null });
    handleLiveOrderStatus(row, 'CONFIRMED');
  };

  const handleVideoPreviewReject = () => {
    const row = videoPreviewData?.row;
    if (!row) return;
    setShowVideoPreview(false);
    setVideoPreviewData({ videoUrl: null, reason: '', comments: '', orderId: '', orderType: null, row: null });
    handleLiveOrderStatus(row, 'REJECTED');
  };

  const closeEvidenceModal = () => {
    if (evidenceLoading) return;
    setShowEvidenceModal(false);
    setEvidenceOrder(null);
    setEvidenceVideoUrl(null);
    setEvidenceReturnReason('');
    setEvidenceReturnComments('');
    setRejectionReason('');
    setCustomReason('');
  };

  const handleEvidenceReject = () => {
    const orderId = evidenceOrder?.id;
    if (!orderId || evidenceLoading) return;
    let finalReason = rejectionReason;
    if (!rejectionReason && customReason && customReason.trim().length >= 10) {
      finalReason = customReason.trim();
    } else if (!rejectionReason) {
      toast.error("Please select a reason or provide a custom reason (min. 10 characters)");
      return;
    }
    setEvidenceAction('REJECT');
    setEvidenceLoading(true);
    rejectOrder({ orderIds: [orderId], reason: finalReason })((response) => {
      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Order rejected successfully");
        setShowEvidenceModal(false);
        setEvidenceOrder(null);
        setEvidenceVideoUrl(null);
        setRejectionReason('');
        setCustomReason('');
        fetchOrdersData();
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
      setEvidenceLoading(false);
      setEvidenceAction(null);
    });
  };

  const handleEvidenceApprove = () => {
    const orderId = evidenceOrder?.id;
    if (!orderId || evidenceLoading) return;
    setEvidenceAction('APPROVE');
    setEvidenceLoading(true);
    approveOrder({ orderIds: [orderId], status: 'CONFIRMED' })((response) => {
      if (response?.data?.statusCode === 200) {
        toast.success(response?.data?.message || "Order accepted successfully");
        setShowEvidenceModal(false);
        setEvidenceOrder(null);
        setEvidenceVideoUrl(null);
        setRejectionReason('');
        setCustomReason('');
        fetchOrdersData();
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
      setEvidenceLoading(false);
      setEvidenceAction(null);
    });
  };

  const renderLiveOrderActions = (row, compact = false) => {
    const type = row?.orderType;
    const status = row?.status;
    const pendingOnlinePayment = isOnlinePaymentPending(row);
    const isLoading = orderActionLoadingId === row?.id;
    const isRejectLoading = isLoading && orderActionLoadingType === 'REJECT';
    const isAcceptLoading = isLoading && orderActionLoadingType === 'ACCEPT';
    const pickupOtp = row?.pidgeOrder?.pickupOtp;
    const dropOtp = row?.pidgeOrder?.dropOtp;
    const showConfirmReject = status === 'INITIATED' && (type === null || type === undefined);
    const showReturnEvidence = status === 'INITIATED' && type === 1;
    const showExchangeActions = status === 'INITIATED' && type === 2;
    const showEvidence = showReturnEvidence; // legacy alias used below
    const isExchange = type === 2;
    const statusForExchange = row?.statusForExchangeOrder;
    const showPickupOtp = status === 'CONFIRMED' && !!pickupOtp && (
      isExchange
        ? statusForExchange === 2
        : ((type === null || type === undefined) && row?.deliveryStatus === 'PENDING')
    );
    const showDropOtp = status === 'CONFIRMED' && !!dropOtp && (
      isExchange
        ? statusForExchange === 1
        : (type === 1 && row?.deliveryStatus === 'DISPATCHED')
    );

    if (!showConfirmReject && !showEvidence && !showExchangeActions && !showPickupOtp && !showDropOtp) return null;

    const buttonStyle = {
      border: '1px solid transparent', borderRadius: compact ? 8 : 6, padding: compact ? '10px 14px' : '7px 12px',
      fontSize: compact ? '12px' : '11px', fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap', cursor: 'pointer',
      width: compact ? '100%' : 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minHeight: compact ? 40 : 30, textAlign: 'center', letterSpacing: '0.01em',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
    };
    const loadingActionStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
    };
    const stableActionBtn = compact
      ? { flex: 1 }
      : { minWidth: 72, width: 72, flex: '0 0 auto' };
    const stop = (event) => event.stopPropagation();
    const otpStyle = {
      ...statusChipStyle('#e0f2fe', '#075985'),
      fontSize: compact ? '15px' : '11px',
      fontWeight: 800,
      padding: compact ? '10px 12px' : '5px 9px',
      width: compact ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      letterSpacing: '0.02em',
    };
    const actionWrapStyle = compact
      ? { display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }
      : { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5, flexWrap: 'wrap' };

    return (
      <div onClick={stop} style={actionWrapStyle}>
        {showPickupOtp && (
          <span title="Share with the pickup driver" style={{ ...otpStyle, background: '#e0f2fe', color: '#075985' }}>
            Pickup OTP: {pickupOtp}
          </span>
        )}
        {showDropOtp && (
          <span title="Share with the delivery driver" style={{ ...otpStyle, background: '#fee2e2', color: '#b91c1c' }}>
            Drop OTP: {dropOtp}
          </span>
        )}
        {showEvidence && (
          <button type="button" style={{ ...buttonStyle, background: '#0e7490', color: '#fff' }} disabled={pendingOnlinePayment || isLoading}
            onClick={() => openVideoPreview(row)}>
            {pendingOnlinePayment ? 'Waiting for Payment' : 'Check Evidence'}
          </button>
        )}
        {showConfirmReject && (
          <div style={compact ? { display: 'flex', gap: 6, width: '100%' } : { display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
            <button type="button" style={{ ...buttonStyle, ...stableActionBtn, background: '#fff1f2', borderColor: '#fecdd3', color: '#be123c', opacity: isRejectLoading ? 0.6 : 1 }} disabled={isLoading || Boolean(orderActionLoadingId)}
              onClick={() => handleLiveOrderStatus(row, 'REJECTED')}>
              {isRejectLoading ? (
                <span style={loadingActionStyle}><Spinner as="span" animation="border" size="sm" /></span>
              ) : 'Reject'}
            </button>
            <button type="button" style={{ ...buttonStyle, ...stableActionBtn, background: '#ccfbf1', borderColor: '#99f6e4', color: '#0f766e', opacity: pendingOnlinePayment || isAcceptLoading || Boolean(orderActionLoadingId) ? 0.6 : 1 }} disabled={pendingOnlinePayment || isLoading || Boolean(orderActionLoadingId)}
              onClick={() => handleLiveOrderStatus(row, 'CONFIRMED')}>
              {isAcceptLoading ? (
                <span style={loadingActionStyle}><Spinner as="span" animation="border" size="sm" /></span>
              ) : pendingOnlinePayment ? 'Waiting for Payment' : 'Accept'}
            </button>
          </div>
        )}
        {showExchangeActions && (
          <button type="button" style={{ ...buttonStyle, background: '#0e7490', color: '#fff' }} disabled={pendingOnlinePayment || isLoading}
            onClick={() => openVideoPreview(row)}>
            {pendingOnlinePayment ? 'Waiting for Payment' : 'Check Evidence'}
          </button>
        )}
      </div>
    );
  };

  const columns = [
    {
      id: 'product',
      name: 'Product',
      cell: (row) => {
        const products = row?.orderProducts || [];
        const firstImg = products.find(p => p?.Product?.thumbnailImageUrl)?.Product?.thumbnailImageUrl;
        const extra = products.length > 1 ? products.length - 1 : 0;
        const needsAction = row?.status === 'INITIATED';
        return (
          <div style={{ position: 'relative', display: 'inline-block' }}>
            {firstImg ? (
              <img
                src={firstImg}
                alt="product"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 4,
                  objectFit: 'cover',
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 4,
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#94a3b8',
                  fontSize: 8,
                  fontWeight: 600,
                }}
              >
                N/A
              </div>
            )}
            {extra > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: needsAction ? undefined : -4,
                  bottom: needsAction ? -4 : undefined,
                  right: -6,
                  minWidth: 14,
                  height: 14,
                  padding: '0 3px',
                  background: '#0f172a',
                  color: '#fff',
                  fontSize: 9,
                  fontWeight: 700,
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                +{extra}
              </span>
            )}
            {needsAction && (
              <span
                className="action-required-icon"
                style={{ position: 'absolute', top: -5, right: -5, width: 15, height: 15 }}
                aria-label="Action required"
                title="Action required"
              >
                <FiAlertTriangle size={9} strokeWidth={3} />
              </span>
            )}
          </div>
        );
      },
      grow: 0.4,
      minWidth: '70px',
      ignoreRowClick: false,
      style: { paddingLeft: '16px', justifyContent: 'flex-start' },
    },
    {
      id: 'orderId',
      name: 'Order Id',
      selector: (row) => row?.orderId,
      cell: (row) => (
        <Tooltip title={row?.orderId} disableInteractive placement="top">
          <div
            style={{
              backgroundColor: row?.isScheduled ? '#fef3c7' : '#f1f5f9',
              color: row?.isScheduled ? '#92400e' : '#0f172a',
              padding: '3px 8px',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '10px',
              cursor: 'pointer',
              textAlign: 'center',
              border: row?.isScheduled ? '1px solid #fbbf24' : '1px solid transparent',
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            onClick={(e) => { e.stopPropagation(); navigate(`/orderdetails/${row.id}`); }}
          >
            {row?.orderId}
          </div>
        </Tooltip>
      ),
      grow: 1,
      minWidth: '130px',
      center: true,
    },
    {
      name: 'Status',
      cell: (row) => {
        const status = row?.status;
        let bg = '#334155', color = '#fff';
        switch (status) {
          case 'CANCELLED': bg = '#dc2626'; color = '#fff'; break;
          case 'INITIATED': bg = '#facc15'; color = '#0f172a'; break;
          case 'CONFIRMED': bg = '#0e7490'; color = '#fff'; break;
          case 'DELIVERED': bg = '#10b981'; color = '#fff'; break;
          case 'REJECTED': bg = '#dc2626'; color = '#fff'; break;
          default: bg = '#6d28d9'; color = '#fff';
        }
        return status ? (
          <span style={statusChipStyle(bg, color)}>
            {status.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
          </span>
        ) : <span style={{ color: '#94a3b8' }}>N/A</span>;
      },
      sortable: true,
      center: true,
      grow: 1,
      minWidth: '110px',
      selector: (row) => row.status,
    },
    {
      id: 'createdAt',
      name: 'Date',
      selector: (row) => moment(row?.createdAt).format('DD/MM/YYYY, hh:mm a'),
      cell: (row) => (
        <Tooltip title={moment(row?.createdAt).format('DD/MM/YYYY, hh:mm a')} disableInteractive placement="top">
          <div style={{ color: '#475569', fontSize: '10px', whiteSpace: 'nowrap' }}>
            {moment(row?.createdAt).format('DD/MM/YY, hh:mm a')}
          </div>
        </Tooltip>
      ),
      grow: 1,
      minWidth: '130px',
      center: true,
    },
    {
      id: 'orderType',
      name: 'Type',
      selector: (row) => row?.orderType,
      cell: (row) => {
        const t = row?.orderType;
        const sfe = row?.statusForExchangeOrder;
        let label;
        if (t === 3) label = 'Delivered';
        else if (t === 2) label = sfe === 1 ? 'EXCHANGE_RETURN' : sfe === 2 ? 'EXCHANGE_DELIVERY' : 'Exchange';
        else if (t === 1) label = 'Return';
        else label = 'New';
        let bg = '#e0f2fe', color = '#0369a1';
        if (t === 1) { bg = '#fee2e2'; color = '#b91c1c'; }
        else if (t === 2) {
          if (sfe === 1) { bg = '#fef3c7'; color = '#92400e'; }
          else if (sfe === 2) { bg = '#dbeafe'; color = '#1d4ed8'; }
          else { bg = '#ede9fe'; color = '#6d28d9'; }
        }
        else if (t === 3) { bg = '#dcfce7'; color = '#166534'; }
        return (
          <span style={{
            display: 'inline-block',
            backgroundColor: bg,
            color,
            padding: '3px 10px',
            borderRadius: '999px',
            fontSize: '10px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            {label}
          </span>
        );
      },
      center: true,
      grow: 1.4,
      minWidth: '150px',
    },
    {
      id: 'amount',
      name: 'Amount',
      selector: (row) => userType === 'ADMIN' ? row?.amount : row?.billingAmount,
      cell: (row) => {
        const amt = userType === 'ADMIN' ? row?.amount : row?.billingAmount;
        return (
          <Tooltip title={amt} disableInteractive placement="top">
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '11px' }}>
              ₹{formatINR(amt)}
            </div>
          </Tooltip>
        );
      },
      center: true,
      grow: 1,
      minWidth: '90px',
    },
    {
      name: 'Payment Status',
      cell: (row) => {
        const status = row?.paymentStatus;
        if (!status) return <span style={{ color: '#94a3b8' }}>N/A</span>;
        const done = status === 'DONE';
        return (
          <span style={statusChipStyle(done ? '#10b981' : '#facc15', done ? '#fff' : '#0f172a')}>
            {done ? 'Completed' : 'Pending'}
          </span>
        );
      },
      center: true,
      grow: 1,
      minWidth: '120px',
      sortable: true,
    },
    {
      name: 'Action',
      cell: (row) => renderLiveOrderActions(row),
      center: true,
      grow: 1.5,
      minWidth: '170px',
      ignoreRowClick: true,
    },
  ];

  const handlePerRowsChange = (newPerPage) => {
    setLimit(newPerPage);
    setPage(1);
    setUpdate(!update);
  };

  const handlePageChange = (page) => {
    setPage(page);
    setUpdate(!update);
  };

  const fetchOrdersData = async () => {
    getHomepageLiveOrders({ page, limit })((response) => {
      if (response?.statusCode === 200) {
        const data = response?.data || {};
        const list = Array.isArray(data) ? data : (data.orders || data.rows || []);
        setOrders(list);
        setTotalRows(data.totalItems ?? data.total ?? list.length);
        setOrdersLoading(false);
      } else {
        setOrders([]);
        setOrdersLoading(false);
      }
    });
  };

  const buildHomepagePayload = (timeRange, startDate, endDate) => {
    if (timeRange === "4") {
      return { startDate, endDate };
    }
    return { months: Number(timeRange) || 0 };
  };

  const fetchHomepageData = (payload) => {
    setDashboardLoading(true);
    getHomepageCmsData(payload)((response) => {
      if (response?.data?.statusCode === 200) {
        const data = response?.data?.data || {};
        setHomepageData({
          sales: data.sales || {},
          revenue: data.revenue || {},
          products: data.products || {},
          vendors: data.vendors || {},
          brands: data.brands || {},
          subSubCategories: data.subSubCategories || {},
          subCategories: data.subCategories || {},
        });
      } else {
        setHomepageData({ sales: {}, revenue: {}, products: {}, vendors: {}, brands: {}, subSubCategories: {}, subCategories: {} });
      }
      setDashboardLoading(false);
    });
  };

  const applyGlobalFilter = (timeRange, startDate, endDate) => {
    const payload = buildHomepagePayload(timeRange, startDate, endDate);
    setGlobalFilter({ timeRange, startDate: startDate || '', endDate: endDate || '' });
    fetchHomepageData(payload);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setOrdersLoading(true);
    try {
      const payload = buildHomepagePayload(globalFilter.timeRange, globalFilter.startDate, globalFilter.endDate);
      fetchHomepageData(payload);
      await fetchOrdersData(filter);
      toast.success('Dashboard refreshed');
    } catch (e) {
      toast.error('Failed to refresh data');
    } finally {
      setTimeout(() => {
        setRefreshing(false);
        setOrdersLoading(false);
      }, 400);
    }
  };

  const handleFilterChange = (filter) => {
    setPage(1);
    setPageActive(1);
    setPageInactive(1);
    setFilter(filter);
  };

  useEffect(() => {
    const w = window.innerWidth;
    if (w < 576) setIsMobile(true);
  }, []);

  useEffect(() => {
    setOrdersLoading(true);
    fetchOrdersData(filter);
  }, [update, filter, filterData, updateDashboard, limit, page]);

  useEffect(() => {
    if (ordersTrigger) {
      fetchOrdersData(filter);
      applyGlobalFilter(globalFilter.timeRange, globalFilter.startDate, globalFilter.endDate);
      setOrdersTrigger(false);
    }
  }, [ordersTrigger]);

  useEffect(() => {
    const d = getDefaultDates();
    applyGlobalFilter("4", d.startDate, d.endDate);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (initial) setUpdate(!update);
      else setInitial(!initial);
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!presetOpen) return;
    const handleDocClick = (e) => {
      if (presetWrapRef.current && !presetWrapRef.current.contains(e.target)) {
        setPresetOpen(false);
      }
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setPresetOpen(false); };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [presetOpen]);

  const conditionalRowStyles = [
    {
      when: row => row.returnIdForVedor !== null,
      style: {
        backgroundColor: 'rgba(239, 68, 68, 0.05)',
        '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.09) !important' },
      },
    },
    {
      when: row => row.exchangeIdForVedor !== null,
      style: {
        backgroundColor: 'rgba(59, 130, 246, 0.05)',
        '&:hover': { backgroundColor: 'rgba(59, 130, 246, 0.09) !important' },
      },
    },
    {
      when: row => row.returnIdForVedor !== null && row.exchangeIdForVedor !== null,
      style: {
        backgroundColor: 'rgba(168, 85, 247, 0.06)',
        '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.1) !important' },
      },
    },
  ];

  const deliveredOrders = salesStats?.totalOrdersDelivered ?? 0;
  const ongoingOrders = (salesStats?.totalOrdersConfirmed ?? 0) + (salesStats?.totalOrderPending ?? 0);
  const deliveredFootOptions = [
    { label: 'Ongoing',   value: formatINR(ongoingOrders),                              color: '#008695' },
    { label: 'Confirmed', value: formatINR(salesStats?.totalOrdersConfirmed ?? 0),      color: '#3b82f6' },
    { label: 'Pending',   value: formatINR(salesStats?.totalOrderPending ?? 0),         color: '#f59e0b' },
    { label: 'Cancelled', value: formatINR(salesStats?.totalOrdersCancelled ?? 0),      color: '#f97316' },
    { label: 'Rejected',  value: formatINR(salesStats?.totalOrdersRejected ?? 0),       color: '#ef4444' },
    { label: 'Returned',  value: formatINR(salesStats?.totalOrdersReturned ?? 0),       color: '#8b5cf6' },
    { label: 'Exchanged', value: formatINR(salesStats?.totalOrdersExchanged ?? 0),      color: '#22c55e' },
  ];

  const totalRevenue = revenueStats?.totalRevenue ?? 0;
  const totalAmount = revenueStats?.totalAmount ?? 0;
  const returnAmount = revenueStats?.returnAmount ?? 0;
  const revenueFootOptions = [
    { label: 'Total',   value: `₹ ${formatINR(totalAmount)}` },
    { label: 'Returns', value: `₹ ${formatINR(returnAmount)}`, color: '#dc2626' },
  ];

  const totalProducts = productStats?.totalProductsListed ?? 0;
  const liveProducts = productStats?.totalLiveProducts ?? 0;
  const lowStockProducts = productStats?.lowStockProducts ?? 0;
  const outOfStockProducts = productStats?.outOfStockProducts ?? 0;

  // Admin-only: vendors + brands
  const activeVendors = vendorStats?.activeVendors ?? 0;
  const verifiedVendors = vendorStats?.verifiedVendors ?? 0;
  const unverifiedVendors = vendorStats?.unverifiedVendors ?? 0;
  const vendorFootOptions = [
    { label: 'Unverified', value: formatINR(unverifiedVendors), color: '#dc2626' },
    { label: 'Verified',   value: formatINR(verifiedVendors) },
  ];
  const totalBrands = brandStats?.totalBrands ?? 0;
  const newBrands = brandStats?.newBrands ?? 0;
  const brandFootOptions = [
    { label: 'New', value: formatINR(newBrands) },
  ];
  const totalSubSubCategories = subSubCategoryStats?.totalSubSubCategories ?? 0;
  const activeSubSubCategories = subSubCategoryStats?.activeSubSubCategories ?? 0;
  const subSubCategoryFootOptions = [
    { label: 'Total', value: formatINR(totalSubSubCategories) },
  ];
  const activeSubCategories = subCategoryStats?.activeSubCategories ?? 0;
  const subCategoryBreakdown = Array.isArray(subCategoryStats?.breakdown) ? subCategoryStats.breakdown : [];
  const subCategoryFootOptions = subCategoryBreakdown.length > 0
    ? subCategoryBreakdown.map((item) => ({
        label: item?.title || '—',
        value: formatINR(item?.subSubCategoriesCount ?? 0),
      }))
    : [{ label: 'Total', value: formatINR(subCategoryStats?.totalSubCategories ?? 0) }];

  return (
    <div className="modern-dashboard">
      <div className="page-header-sticky dashboard-header">
        <div className="dashboard-greeting">
          <div className="dashboard-greeting-row">
            <div className="dashboard-greeting-text-block">
              <h3 className="dashboard-greeting-text" style={HOME_FONT.greeting}>
                {greetingPrefix}, <span style={{ color: '#008695' }}>{greetingName}</span> 👋
              </h3>
              <p style={HOME_FONT.subheading}>
                {globalFilter.startDate && globalFilter.endDate
                  ? (() => {
                      const compact = (iso) => {
                        const s = formatDdMmYy(iso);
                        const parts = s.split(' ');
                        if (parts.length === 3) parts[2] = parts[2].slice(2);
                        return parts.join(' ');
                      };
                      return `Dashboard · ${compact(globalFilter.startDate)} → ${compact(globalFilter.endDate)}`;
                    })()
                  : greetingSubtitle}
              </p>
            </div>
            {/* Mobile-only inline icon buttons */}
            <div className="dashboard-greeting-actions">
              <button
                type="button"
                className="dgi-btn"
                onClick={openDateSheet}
                aria-label="Choose date range"
              >
                <FiCalendar size={15} />
              </button>
              <button
                type="button"
                className="dgi-btn"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label="Refresh"
              >
                <FiRefreshCw size={15} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              </button>
            </div>
          </div>
        </div>

        <div className="filters-row">
          {/* Preset dropdown — custom menu so we can style the open list (native <option> can't be styled) */}
          {(() => {
            const activePreset = HOMEPAGE_PRESETS.find((p) => {
              const { start, end } = computePresetRange(p);
              return start === globalFilter.startDate && end === globalFilter.endDate;
            });
            const triggerLabel = activePreset ? activePreset.label : 'Custom Range';
            return (
              <div className="modern-dd filters-preset" ref={presetWrapRef}>
                <button
                  type="button"
                  className={`modern-dd-trigger${presetOpen ? ' is-open' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={presetOpen}
                  onClick={() => setPresetOpen((v) => !v)}
                >
                  <FiCalendar size={13} className="modern-dd-lead" />
                  <span className="modern-dd-label">{triggerLabel}</span>
                  <svg
                    className={`modern-dd-caret${presetOpen ? ' is-open' : ''}`}
                    width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="#008695" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {presetOpen && (
                  <div className="modern-dd-menu" role="listbox">
                    {HOMEPAGE_PRESETS.map((p) => {
                      const isActive = activePreset && activePreset.key === p.key;
                      return (
                        <button
                          key={p.key}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          className={`modern-dd-item${isActive ? ' is-active' : ''}`}
                          onClick={() => {
                            const { start, end } = computePresetRange(p);
                            setGlobalFilter((prev) => ({ ...prev, startDate: start, endDate: end, timeRange: '4' }));
                            applyGlobalFilter('4', start, end);
                            setPresetOpen(false);
                          }}
                        >
                          <span>{p.label}</span>
                          {isActive && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#008695" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </button>
                      );
                    })}
                    <div className="modern-dd-divider" />
                    <button
                      type="button"
                      role="option"
                      aria-selected={!activePreset}
                      className={`modern-dd-item modern-dd-item-muted${!activePreset ? ' is-active' : ''}`}
                      onClick={() => setPresetOpen(false)}
                    >
                      <span>Custom Range</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })()}

          <span className="modern-date-wrap">
            <input
              type="date"
              className="modern-date"
              style={HOME_FONT.filterCtrl}
              value={globalFilter.startDate}
              onClick={(e) => { try { e.currentTarget.showPicker && e.currentTarget.showPicker(); } catch (_) {} }}
              onChange={(e) => {
                const fromDate = e.target.value;
                if (fromDate && globalFilter.endDate && fromDate > globalFilter.endDate) {
                  toast.error('Start date cannot be after end date');
                  return;
                }
                setGlobalFilter(prev => ({ ...prev, startDate: fromDate, timeRange: "4" }));
                if (fromDate && globalFilter.endDate && fromDate <= globalFilter.endDate) {
                  applyGlobalFilter("4", fromDate, globalFilter.endDate);
                }
              }}
              max={globalFilter.endDate || undefined}
            />
            <span className="modern-date-display" aria-hidden="true">{formatDdMmYy(globalFilter.startDate) || 'Start date'}</span>
          </span>
          <span className="text-muted small filters-to">to</span>
          <span className="modern-date-wrap">
            <input
              type="date"
              className="modern-date"
              style={HOME_FONT.filterCtrl}
              value={globalFilter.endDate}
              onClick={(e) => { try { e.currentTarget.showPicker && e.currentTarget.showPicker(); } catch (_) {} }}
              onChange={(e) => {
                const toDate = e.target.value;
                if (toDate && globalFilter.startDate && toDate < globalFilter.startDate) {
                  toast.error('End date cannot be before start date');
                  return;
                }
                setGlobalFilter(prev => ({ ...prev, endDate: toDate, timeRange: "4" }));
                if (globalFilter.startDate && toDate && toDate >= globalFilter.startDate) {
                  applyGlobalFilter("4", globalFilter.startDate, toDate);
                }
              }}
              min={globalFilter.startDate || undefined}
            />
            <span className="modern-date-display" aria-hidden="true">{formatDdMmYy(globalFilter.endDate) || 'End date'}</span>
          </span>

          <button
            onClick={handleRefresh}
            className="btn modern-refresh d-flex align-items-center gap-2"
            style={HOME_FONT.filterCtrl}
            disabled={refreshing}
          >
            <FiRefreshCw size={16} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      <div className="modern-stat-grid">
        <StatCard
          icon={FaRupeeSign}
          label="Revenue"
          value={`₹ ${formatINR(totalRevenue)}`}
          accent={{ bg: '#ecfdf5', fg: '#6ee7b7' }}
          footOptions={revenueFootOptions}
          autoCycle={false}
          onNavigate={() => navigate('/orders')}
        />
        <StatCard
          icon={FiTruck}
          label="Orders"
          value={formatINR(deliveredOrders)}
          accent={{ bg: '#eef2ff', fg: '#818cf8' }}
          footOptions={deliveredFootOptions}
          onNavigate={() => navigate('/orders')}
        />
        <StatCard
          icon={FiLayers}
          label="Live Products"
          value={formatINR(liveProducts)}
          accent={{ bg: '#f5f3ff', fg: '#c4b5fd' }}
          footLabel="Total"
          footValue={formatINR(totalProducts)}
          onNavigate={() => navigate('/inventory')}
        />
        <StatCard
          icon={FiAlertTriangle}
          label="Out of Stock"
          value={formatINR(outOfStockProducts)}
          accent={{ bg: '#fef2f2', fg: '#fca5a5' }}
          footLabel="Low Stock"
          footValue={formatINR(lowStockProducts)}
          onNavigate={() => navigate('/inventory')}
        />
        {userType === 'ADMIN' && (
          <>
            <StatCard
              icon={FaStore}
              label="Vendors"
              value={formatINR(activeVendors)}
              accent={{ bg: '#ecfeff', fg: '#67e8f9' }}
              footOptions={vendorFootOptions}
              autoCycle={false}
              onNavigate={() => navigate('/vendors')}
            />
            <StatCard
              icon={FiTag}
              label="Brands"
              value={formatINR(totalBrands)}
              accent={{ bg: '#fff7ed', fg: '#fdba74' }}
              footOptions={brandFootOptions}
              autoCycle={false}
              onNavigate={() => navigate('/brand')}
            />
            <StatCard
              icon={FiFolder}
              label="Category"
              value={formatINR(activeSubCategories)}
              accent={{ bg: '#eff6ff', fg: '#93c5fd' }}
              footOptions={subCategoryFootOptions}
              autoCycle={true}
              cycleMs={8000}
              footSwapVariant="vscroll"
              onNavigate={() => navigate('/categories')}
            />
            <StatCard
              icon={FiGrid}
              label="SubCategory"
              value={formatINR(activeSubSubCategories)}
              accent={{ bg: '#f0fdf4', fg: '#86efac' }}
              footOptions={subSubCategoryFootOptions}
              autoCycle={false}
              onNavigate={() => navigate('/categories')}
            />
          </>
        )}
      </div>

      <div className="modern-panel mt-3 live-orders-panel">
        <style>{`
          @keyframes liveDotBlink {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.35; transform: scale(0.85); }
          }
          @keyframes liveDotPulse {
            0%   { transform: scale(1);   opacity: 0.55; }
            80%  { transform: scale(2.6); opacity: 0; }
            100% { transform: scale(2.6); opacity: 0; }
          }
          .live-dot-wrap {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 10px; height: 10px;
            margin-right: 6px;
          }
          .live-dot-core {
            width: 8px; height: 8px;
            border-radius: 50%;
            background: #dc2626;
            box-shadow: 0 0 0 2px rgba(220,38,38,0.15);
            animation: liveDotBlink 1.2s ease-in-out infinite;
          }
          .live-dot-ring {
            position: absolute;
            inset: 0;
            border-radius: 50%;
            background: rgba(220,38,38,0.55);
            animation: liveDotPulse 1.6s ease-out infinite;
          }
          @keyframes actionShake {
            0%, 60%, 100% { transform: rotate(0deg); }
            5%  { transform: rotate(-14deg); }
            10% { transform: rotate(12deg); }
            15% { transform: rotate(-10deg); }
            20% { transform: rotate(8deg); }
            25% { transform: rotate(-5deg); }
            30% { transform: rotate(3deg); }
            35% { transform: rotate(0deg); }
          }
          .action-required-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: #dc2626;
            color: #fff;
            border-radius: 50%;
            border: 1.5px solid #fff;
            transform-origin: center;
            animation: actionShake 1.6s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .action-required-icon { animation: none; }
          }
        `}</style>
        <div className="modern-panel-header">
          <div>
            <h5 style={{ ...HOME_FONT.panelTitle, display: 'inline-flex', alignItems: 'center' }}>
              <span className="live-dot-wrap" aria-hidden="true">
                <span className="live-dot-ring" />
                <span className="live-dot-core" />
              </span>
              Live Ongoing Orders
            </h5>
          </div>
        </div>

        <Box className="products-and-options live-orders-table" sx={{ mt: 1 }}>
          <Paper square className="shadow-none border-0">
            <DataTable
              columns={columns}
              conditionalRowStyles={conditionalRowStyles}
              data={orders}
              progressPending={ordersLoading}
              progressComponent={<div className="live-orders-loading" style={{ minHeight: '60vh' }}><Loader /></div>}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={limit}
              paginationRowsPerPageOptions={[10, 20, 50, 100]}
              paginationDefaultPage={page}
              onRowClicked={(row) => navigate(`/orderdetails/${row?.id}`)}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              currentPage={page}
              customStyles={queueDatatableCustomStyles}
              noDataComponent={
                <div className="live-orders-empty">
                  <div className="live-orders-empty-icon" aria-hidden="true">📦</div>
                  <div className="live-orders-empty-title">No ongoing orders currently</div>
                  <div className="live-orders-empty-sub">You can see all your ongoing orders here</div>
                </div>
              }
            />
          </Paper>
        </Box>

        {/* Mobile card list — mirrors the Orders page layout */}
        <div className="live-orders-mobile-list">
          {ordersLoading ? (
            <div className="live-orders-loading" style={{ minHeight: '40vh' }}><Loader /></div>
          ) : orders && orders.length > 0 ? (
            orders.map((row) => {
              const status = row?.status;
              let chipBg = '#334155', chipColor = '#fff';
              switch (status) {
                case 'CANCELLED': chipBg = '#dc2626'; chipColor = '#fff'; break;
                case 'INITIATED': chipBg = '#facc15'; chipColor = '#0f172a'; break;
                case 'CONFIRMED': chipBg = '#0e7490'; chipColor = '#fff'; break;
                case 'DELIVERED': chipBg = '#10b981'; chipColor = '#fff'; break;
                case 'REJECTED': chipBg = '#dc2626'; chipColor = '#fff'; break;
                default: chipBg = '#6d28d9'; chipColor = '#fff';
              }
              const chipLabel = status ? status.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;

              const t = row?.orderType;
              const sfe = row?.statusForExchangeOrder;
              const typeLabel = t === 3
                ? 'Delivered'
                : t === 2
                  ? (sfe === 1 ? 'EXCHANGE_RETURN' : sfe === 2 ? 'EXCHANGE_DELIVERY' : 'Exchange')
                  : t === 1
                    ? 'Return'
                    : 'New';
              let typeBg = '#e0f2fe', typeColor = '#0369a1';
              if (t === 1) { typeBg = '#fee2e2'; typeColor = '#b91c1c'; }
              else if (t === 2) {
                if (sfe === 1) { typeBg = '#fef3c7'; typeColor = '#92400e'; }
                else if (sfe === 2) { typeBg = '#dbeafe'; typeColor = '#1d4ed8'; }
                else { typeBg = '#ede9fe'; typeColor = '#6d28d9'; }
              }
              else if (t === 3) { typeBg = '#dcfce7'; typeColor = '#166534'; }

              const amt = userType === 'ADMIN' ? row?.amount : row?.billingAmount;
              const done = row?.paymentStatus === 'DONE';
              const products = row?.orderProducts || [];
              const firstImg = products.find(p => p?.Product?.thumbnailImageUrl)?.Product?.thumbnailImageUrl;
              const extra = products.length > 1 ? products.length - 1 : 0;

              return (
                <div
                  key={row.id}
                  className="live-order-card"
                  onClick={() => navigate('/orderdetails/' + row?.id)}
                >
                  <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                    {/* Image on the left */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <span
                        style={{
                          position: 'absolute',
                          top: 6, left: 6, zIndex: 2,
                          display: 'inline-block',
                          backgroundColor: typeBg,
                          color: typeColor,
                          padding: '2px 6px',
                          borderRadius: '999px',
                          fontSize: '8px',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          boxShadow: '0 1px 3px rgba(15,23,42,0.15)',
                        }}
                      >
                        {typeLabel}
                      </span>
                      {firstImg ? (
                        <img
                          src={firstImg}
                          alt="product"
                          style={{
                            width: 96, height: 100,
                            borderRadius: 6,
                            objectFit: 'cover',
                            border: '1px solid #e2e8f0',
                            display: 'block',
                          }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 96, height: 100,
                            borderRadius: 6,
                            background: '#f1f5f9',
                            border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#94a3b8', fontSize: 9, fontWeight: 600,
                          }}
                        >
                          N/A
                        </div>
                      )}
                      {extra > 0 && (
                        <span
                          style={{
                            position: 'absolute',
                            top: status === 'INITIATED' ? undefined : -4,
                            bottom: status === 'INITIATED' ? -4 : undefined,
                            right: -6,
                            minWidth: 18, height: 18,
                            padding: '0 5px',
                            background: '#0f172a',
                            color: '#fff',
                            fontSize: 10, fontWeight: 700,
                            borderRadius: 999,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            lineHeight: 1,
                          }}
                        >
                          +{extra}
                        </span>
                      )}
                      {status === 'INITIATED' && (
                        <span
                          className="action-required-icon"
                          style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, zIndex: 3 }}
                          aria-label="Action required"
                          title="Action required"
                        >
                          <FiAlertTriangle size={13} strokeWidth={2.8} />
                        </span>
                      )}
                    </div>

                    {/* Labels stacked on the right */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="live-order-card-id">#{row?.orderId}</div>
                          <div className="live-order-card-date">
                            {moment(row?.createdAt).format('DD MMM YY, hh:mm a')}
                          </div>
                        </div>
                        {chipLabel && (
                          <span
                            className="live-order-card-chip"
                            style={{ background: chipBg, color: chipColor, flexShrink: 0 }}
                          >
                            {chipLabel}
                          </span>
                        )}
                      </div>

                      {/* Price — prominent, right under the id+date row */}
                      <div className="live-order-card-price">₹{formatINR(amt)}</div>

                      {/* Payment method + status — split pill */}
                      {(() => {
                        const mode = (row?.paymentMode || '').toUpperCase();
                        const isCOD = mode === 'COD';
                        const modeLabel = isCOD ? 'COD' : mode ? 'Online' : '—';
                        const modeBg = '#f1f5f9';
                        const modeColor = '#475569';
                        const statusBg = done ? '#f0fdf4' : '#fffbeb';
                        const statusColor = done ? '#15803d' : '#b45309';
                        const statusDot = done ? '#16a34a' : '#d97706';
                        const statusLabel = done ? 'Paid' : 'Pending';
                        return (
                          <div className="live-order-card-pay" style={{ width: 'fit-content', justifyContent: 'flex-start', gap: 6 }}>
                            <span
                              className="live-order-card-pay-mode"
                              style={{ background: modeBg, color: modeColor }}
                            >
                              {modeLabel}
                            </span>
                            <span
                              className="live-order-card-pay-status"
                              style={{ background: statusBg, color: statusColor }}
                            >
                              <span className="live-order-card-pay-dot" style={{ background: statusDot }} />
                              {statusLabel}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div style={{ width: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' }}>
                    {renderLiveOrderActions(row, true)}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="live-orders-empty">
              <div className="live-orders-empty-icon" aria-hidden="true">📦</div>
              <div className="live-orders-empty-title">No ongoing orders currently</div>
              <div className="live-orders-empty-sub">You can see all your ongoing orders here</div>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showRejectConfirm}
        onHide={() => !orderActionLoadingId && setShowRejectConfirm(false)}
        centered
        backdrop={orderActionLoadingId ? 'static' : true}
        keyboard={!orderActionLoadingId}
        dialogClassName={`dashboard-reject-order-modal${(pendingRejectOrder?.orderType === 1 || pendingRejectOrder?.orderType === 2) ? ' is-with-reason' : ''}`}
        contentClassName="dashboard-reject-order-modal-content"
      >
        <style>{`
          .dashboard-reject-order-modal { max-width: 380px; }
          .dashboard-reject-order-modal.is-with-reason { max-width: 460px; }
          .dashboard-reject-order-modal-content {
            border: none !important;
            border-radius: 14px !important;
            box-shadow: 0 20px 50px rgba(15,23,42,0.18) !important;
            overflow: hidden !important;
          }
          @media (max-width: 480px) {
            .dashboard-reject-order-modal,
            .dashboard-reject-order-modal.is-with-reason { max-width: none !important; width: calc(100vw - 24px) !important; margin: 0 auto !important; }
          }
        `}</style>
        <Modal.Body style={{ padding: '22px 22px 18px', textAlign: 'center' }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            <FiX size={22} />
          </div>
          <h5 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.01em' }}>
            Reject {pendingRejectOrder?.orderType === 2 ? 'exchange ' : pendingRejectOrder?.orderType === 1 ? 'return ' : ''}order?
          </h5>
          <p style={{ fontSize: 12.5, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
            This action cannot be undone. The customer will be notified that their order has been rejected.
          </p>
          {(pendingRejectOrder?.orderType === 1 || pendingRejectOrder?.orderType === 2) && (
            <div style={{ marginTop: 18, textAlign: 'left', borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
              <label style={{ display: 'block', marginBottom: 5, fontSize: 11, fontWeight: 600, color: '#475569', letterSpacing: '0.02em' }}>
                Rejection reason
              </label>
              <Form.Select
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                disabled={Boolean(orderActionLoadingId)}
                style={{ fontSize: 12, padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', color: '#0f172a', marginBottom: 10 }}
              >
                <option value="">Choose a rejection reason...</option>
                {EVIDENCE_REJECTION_REASONS.map((reason, idx) => (
                  <option key={idx} value={reason}>{reason}</option>
                ))}
              </Form.Select>
              <label style={{ display: 'block', marginBottom: 5, fontSize: 11, fontWeight: 600, color: '#475569', letterSpacing: '0.02em' }}>
                Any other reason
              </label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Provide specific reason for rejection (minimum 10 characters)..."
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                disabled={Boolean(orderActionLoadingId)}
                style={{ fontSize: 12, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1', color: '#0f172a', minHeight: 72, resize: 'vertical' }}
              />
              <div style={{ marginTop: 4, fontSize: 10, textAlign: 'right', color: customReason && customReason.trim().length >= 10 ? '#16a34a' : '#94a3b8' }}>
                {customReason?.length || 0}/10 characters
                {customReason && customReason.trim().length >= 10 && ' ✓'}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            padding: '12px 18px',
            borderTop: '1px solid #eef2f7',
            display: 'flex',
            gap: 8,
            justifyContent: 'stretch',
          }}
        >
          <button
            type="button"
            onClick={() => {
              setShowRejectConfirm(false);
              setPendingRejectOrder(null);
            }}
            disabled={orderActionLoadingId}
            style={{
              flex: 1,
              height: 38,
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#0f172a',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: orderActionLoadingId ? 'not-allowed' : 'pointer',
              opacity: orderActionLoadingId ? 0.6 : 1,
              transition: 'all 0.15s ease',
            }}
          >
            No
          </button>
          {(() => {
            const needsReason = pendingRejectOrder?.orderType === 1 || pendingRejectOrder?.orderType === 2;
            const reasonReady = !needsReason || (rejectionReason || (customReason && customReason.trim().length >= 10));
            const disabled = Boolean(orderActionLoadingId) || !reasonReady;
            return (
              <button
                type="button"
                onClick={confirmRejectOrder}
                disabled={disabled}
                style={{
                  flex: 1,
                  height: 38,
                  borderRadius: 6,
                  border: '1px solid #dc2626',
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  boxShadow: '0 3px 8px rgba(220,38,38,0.22)',
                  transition: 'all 0.15s ease',
                }}
              >
                {orderActionLoadingId ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <FiX size={13} />
                    Yes, Reject
                  </>
                )}
              </button>
            );
          })()}
        </Modal.Footer>
      </Modal>

      {/* Approve Confirmation Modal — reviews the products before calling approve */}
      <Modal
        show={showApproveConfirm}
        onHide={() => !orderActionLoadingId && setShowApproveConfirm(false)}
        centered
        size="md"
        backdrop={orderActionLoadingId ? 'static' : true}
        keyboard={!orderActionLoadingId}
        dialogClassName="approve-order-modal"
      >
        <style>{`
          .approve-order-modal .modal-dialog { max-width: 520px; }
          .approve-order-modal .modal-content { border: 0; border-radius: 12px; overflow: hidden; }
          .approve-order-modal .modal-header {
            display: flex !important;
            align-items: center !important;
            justify-content: space-between !important;
            padding: 12px 20px !important;
            min-height: 52px !important;
            border-bottom: 1px solid #e5e7eb;
          }
          .approve-order-modal .modal-header .modal-title {
            margin: 0 !important;
            padding: 0 !important;
            display: inline-block !important;
            line-height: 1.2 !important;
            white-space: normal;
          }
          .approve-order-modal .modal-header .btn-close {
            margin: 0 !important;
            padding: 8px !important;
            align-self: center !important;
          }
          .approve-order-modal .modal-body { padding: 14px 20px; max-height: 60vh; overflow-y: auto; background: #f8fafc; }
          .approve-order-modal .modal-footer {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            padding: 10px 16px !important;
            min-height: 54px !important;
            border-top: 1px solid #e5e7eb;
            background: #fff;
            gap: 8px;
          }
          .approve-order-modal .modal-footer > * { margin: 0 !important; }
          .approve-order-modal .ao-title {
            display: inline-block;
            margin: 0;
            padding: 0;
            font-size: 14px;
            font-weight: 700;
            color: #0f172a;
            letter-spacing: -0.01em;
            line-height: 1.2;
            vertical-align: middle;
          }
          .approve-order-modal .ao-product {
            display: flex;
            gap: 12px;
            padding: 10px;
            background: #fff;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            margin-bottom: 8px;
          }
          .approve-order-modal .ao-product:last-child { margin-bottom: 0; }
          .approve-order-modal .ao-product-img,
          .approve-order-modal .ao-product-img-fallback {
            width: 64px; height: 64px; flex-shrink: 0;
            border-radius: 8px; object-fit: cover;
            border: 1px solid #e2e8f0; background: #f1f5f9;
          }
          .approve-order-modal .ao-product-img-fallback {
            display: inline-flex; align-items: center; justify-content: center;
            color: #94a3b8; font-size: 10px; font-weight: 600;
          }
          .approve-order-modal .ao-product-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
          .approve-order-modal .ao-product-name {
            margin: 0;
            font-size: 12.5px;
            font-weight: 700;
            color: #0f172a;
            letter-spacing: -0.01em;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .approve-order-modal .ao-product-store {
            display: block;
            margin-top: 2px;
            margin-bottom: 8px;
            font-size: 10px;
            font-weight: 600;
            color: #64748b;
            line-height: 1.2;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .approve-order-modal .ao-product-meta {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 4px 6px;
            font-size: 10px;
            color: #475569;
          }
          .approve-order-modal .ao-name-store-gap { height: 8px; }
          .approve-order-modal .ao-chip {
            display: inline-flex;
            align-items: center;
            gap: 3px;
            padding: 2px 7px;
            border-radius: 999px;
            background: #f1f5f9;
            font-weight: 600;
            white-space: nowrap;
          }
          .approve-order-modal .ao-chip-size {
            background: #e0f2fe;
            color: #0369a1;
            font-size: 12px !important;
            font-weight: 800 !important;
            padding: 4px 10px !important;
            letter-spacing: 0.03em;
            border: 1.5px solid #7dd3fc;
            box-shadow: 0 1px 2px rgba(3, 105, 161, 0.12);
            text-transform: uppercase;
          }
          .approve-order-modal .ao-color-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 2px 8px 2px 4px;
            border-radius: 999px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            font-weight: 600;
            color: #334155;
            white-space: nowrap;
          }
          .approve-order-modal .ao-color-swatch {
            width: 14px;
            height: 14px;
            border-radius: 50%;
            border: 1px solid rgba(15, 23, 42, 0.15);
            box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4);
            flex-shrink: 0;
          }
          .approve-order-modal .ao-product-price {
            margin-left: auto;
            padding-left: 8px;
            font-size: 12.5px;
            font-weight: 800;
            color: #0f172a;
            font-variant-numeric: tabular-nums;
            flex-shrink: 0;
          }
          .approve-order-modal .ao-total-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 12px;
            margin-top: 8px;
            background: #ecfdf5;
            border: 1px solid #bbf7d0;
            border-radius: 10px;
            font-size: 12px;
            font-weight: 700;
            color: #166534;
          }
          .approve-order-modal .ao-total-row .ao-total-value {
            font-size: 14px;
            font-variant-numeric: tabular-nums;
          }
        `}</style>
        <Modal.Header closeButton={!orderActionLoadingId}>
          <Modal.Title as="span" className="ao-title">
            {(() => {
              const t = pendingApproveOrder?.orderType;
              const sfe = pendingApproveOrder?.statusForExchangeOrder;
              let kindLabel = 'new';
              let kindColor = '#0369a1';
              if (t === 2) {
                if (sfe === 1) { kindLabel = 'exchange return'; kindColor = '#b45309'; }
                else if (sfe === 2) { kindLabel = 'exchange delivery'; kindColor = '#1d4ed8'; }
                else { kindLabel = 'exchange'; kindColor = '#6d28d9'; }
              } else if (t === 1) {
                kindLabel = 'return'; kindColor = '#b91c1c';
              } else {
                kindLabel = 'new'; kindColor = '#0369a1';
              }
              return (
                <>
                  Approve{' '}
                  <span style={{ color: kindColor, fontWeight: 800, textTransform: 'capitalize' }}>{kindLabel}</span>
                  {' '}order {pendingApproveOrder?.orderId}?
                </>
              );
            })()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(pendingApproveOrder?.orderProducts || []).length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No products in this order.</div>
          ) : (
            (pendingApproveOrder?.orderProducts || []).map((item, i) => {
              const p = item?.Product || {};
              const img = p?.thumbnailImageUrl || p?.images?.[0]?.image_url || '';
              const size = item?.VariationSize?.size || item?.productParameter?.size || p?.size || null;
              const price = item?.pricePaidByCustomer ?? item?.price ?? 0;
              return (
                <div key={item?.id || i} className="ao-product">
                  {img ? (
                    <img src={img} alt={p?.productName || 'product'} className="ao-product-img"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <span className="ao-product-img-fallback">N/A</span>
                  )}
                  <div className="ao-product-body">
                    <h6 className="ao-product-name">{p?.productName || 'Unnamed product'}</h6>
                    {userType === 'ADMIN' && p?.manufacturer?.storeName ? (
                      <span className="ao-product-store" title={p.manufacturer.storeName}>
                        {p.manufacturer.storeName}
                      </span>
                    ) : (
                      <span className="ao-name-store-gap" aria-hidden="true" />
                    )}
                    <div className="ao-product-meta">
                      {p?.color && (
                        <span className="ao-color-chip" title={p.color}>
                          <span className="ao-color-swatch" style={{ background: p.color }} />
                          {p.color}
                        </span>
                      )}
                      {(size || item?.quantity != null) && (
                        <span className="ao-chip ao-chip-size">
                          {size || 'N/A'} × {item?.quantity ?? 1}
                        </span>
                      )}
                      <span className="ao-product-price">₹{formatINR(price)}</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          {(() => {
            const orderTotal = userType === 'ADMIN' ? pendingApproveOrder?.amount : pendingApproveOrder?.billingAmount;
            if (orderTotal == null) return null;
            return (
              <div className="ao-total-row">
                <span>Order total</span>
                <span className="ao-total-value">₹{formatINR(orderTotal)}</span>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" onClick={confirmApproveOrder} disabled={orderActionLoadingId}
            style={{ padding: '6px 16px', height: 34, borderRadius: 6, border: 0, background: '#008695', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, boxShadow: '0 3px 8px rgba(0, 134, 149, 0.22)' }}>
            {orderActionLoadingId ? (<><Spinner as="span" animation="border" size="sm" /> Approving...</>) : (<>Confirm &amp; Approve</>)}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Video Preview Modal — evidence-only view for exchange orders */}
      <Modal
        show={showVideoPreview}
        onHide={closeVideoPreview}
        centered
        size="md"
        dialogClassName="video-preview-modal"
      >
        <style>{`
          .video-preview-modal .modal-dialog { max-width: 560px; }
          .video-preview-modal .modal-content { border: 0; border-radius: 12px; overflow: hidden; }
          .video-preview-modal .modal-header {
            display: flex !important;
            align-items: center !important;
            padding: 12px 20px !important;
            min-height: 52px !important;
            border-bottom: 1px solid #e5e7eb;
          }
          .video-preview-modal .modal-header .modal-title { margin: 0 !important; padding: 0 !important; display: inline-flex !important; align-items: center !important; }
          .video-preview-modal .modal-header .btn-close { margin: 0 !important; padding: 8px !important; align-self: center !important; }
          .video-preview-modal .vp-title { font-size: 14px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; line-height: 1; }
          .video-preview-modal .modal-body { padding: 16px 20px; background: #f8fafc; }
          .video-preview-modal .vp-video-frame {
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            overflow: hidden;
            background: #000;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 14px;
          }
          .video-preview-modal .vp-video-empty {
            border: 1px dashed #cbd5e1;
            border-radius: 10px;
            background: #fff;
            height: 300px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 6px;
            color: #94a3b8;
            text-align: center;
            padding: 20px;
            margin-bottom: 14px;
          }
          .video-preview-modal .vp-video-empty p { margin: 0; font-size: 12px; color: #64748b; }
          .video-preview-modal .vp-info-block {
            padding: 12px 14px;
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 10px;
          }
          .video-preview-modal .vp-info-row { display: flex; gap: 8px; align-items: flex-start; }
          .video-preview-modal .vp-info-row + .vp-info-row { margin-top: 8px; }
          .video-preview-modal .vp-info-label {
            flex-shrink: 0;
            font-size: 10px;
            font-weight: 600;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            min-width: 72px;
            padding-top: 2px;
          }
          .video-preview-modal .vp-info-value {
            font-size: 12px;
            font-weight: 500;
            color: #0f172a;
            line-height: 1.45;
            word-wrap: break-word;
            overflow-wrap: anywhere;
          }
          .video-preview-modal .vp-info-empty { color: #94a3b8; font-weight: 400; font-style: italic; }
          .video-preview-modal .modal-footer {
            display: flex !important;
            align-items: center !important;
            justify-content: flex-end !important;
            padding: 10px 16px !important;
            min-height: 54px !important;
            border-top: 1px solid #e5e7eb;
            background: #fff;
            gap: 8px;
          }
          .video-preview-modal .modal-footer > * { margin: 0 !important; }
          .video-preview-modal .vp-btn {
            padding: 6px 16px;
            height: 34px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            cursor: pointer;
            transition: all 0.15s ease;
          }
          .video-preview-modal .vp-btn-reject { border: 1px solid #fecdd3; background: #fff1f2; color: #be123c; }
          .video-preview-modal .vp-btn-reject:hover { background: #ffe4e6; }
          .video-preview-modal .vp-btn-accept { border: 0; background: #008695; color: #fff; box-shadow: 0 3px 8px rgba(0, 134, 149, 0.22); }
          .video-preview-modal .vp-btn-accept:hover { background: #007784; }
          @media (max-width: 575.98px) {
            .video-preview-modal .modal-dialog { margin: 8px; max-width: none; }
            .video-preview-modal .vp-video-frame,
            .video-preview-modal .vp-video-empty { height: 220px; }
          }
        `}</style>
        <Modal.Header closeButton>
          <Modal.Title as="span" className="vp-title">
            {videoPreviewData.orderType === 2 ? 'Exchange' : videoPreviewData.orderType === 1 ? 'Return' : ''} Evidence · {videoPreviewData.orderId}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {videoPreviewData.videoUrl ? (
            <div className="vp-video-frame">
              <video
                key={videoPreviewData.videoUrl}
                controls
                autoPlay
                muted
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              >
                <source src={videoPreviewData.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <div className="vp-video-empty">
              <FiImage size={32} />
              <p>No evidence video available</p>
            </div>
          )}
          <div className="vp-info-block">
            <div className="vp-info-row">
              <span className="vp-info-label">Reason</span>
              <span className={`vp-info-value${videoPreviewData.reason ? '' : ' vp-info-empty'}`}>
                {videoPreviewData.reason || 'Not provided'}
              </span>
            </div>
            <div className="vp-info-row">
              <span className="vp-info-label">Comment</span>
              <span className={`vp-info-value${videoPreviewData.comments ? '' : ' vp-info-empty'}`}>
                {videoPreviewData.comments || 'No comment provided'}
              </span>
            </div>
          </div>
        </Modal.Body>
        {videoPreviewData?.row && (
          <Modal.Footer>
            <button type="button" className="vp-btn vp-btn-reject" onClick={handleVideoPreviewReject}>
              <FiX size={13} />
              Reject
            </button>
            <button type="button" className="vp-btn vp-btn-accept" onClick={handleVideoPreviewAccept}>
              Accept
            </button>
          </Modal.Footer>
        )}
      </Modal>

      {/* Evidence Check Modal (shared UX with OrderDetails page) */}
      <Modal
        show={showEvidenceModal}
        onHide={closeEvidenceModal}
        centered
        size="lg"
        dialogClassName="evidence-modal"
        backdrop={evidenceLoading ? 'static' : true}
        keyboard={!evidenceLoading}
      >
        <style>{`
          .evidence-modal .modal-dialog { max-width: 720px; }
          .evidence-modal .modal-content { border: 0; border-radius: 12px; overflow: hidden; }
          .evidence-modal .modal-header { padding: 14px 20px !important; border-bottom: 1px solid #e5e7eb; }
          .evidence-modal .modal-header .modal-title { margin: 0 !important; padding: 0 !important; display: inline-flex; align-items: center; }
          .evidence-modal .modal-header .btn-close { margin: 0 !important; padding: 8px !important; font-size: 12px; align-self: center; }
          .evidence-modal .modal-body { padding: 0; max-height: 68vh; overflow-y: auto; }
          .evidence-modal .modal-footer { padding: 10px 16px; border-top: 1px solid #e5e7eb; background: #f8fafc; }
          .evidence-modal .evidence-title { display: inline-block; margin: 0; padding: 0; font-size: 14px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; line-height: 1.2; vertical-align: middle; }
          .evidence-modal .evidence-col-left { padding: 14px 16px; border-right: 1px solid #e5e7eb; background: #fff; }
          .evidence-modal .evidence-col-right { padding: 14px 16px; background: #f8fafc; }
          .evidence-modal .evidence-section-label { margin: 0 0 8px; font-size: 12px; font-weight: 600; color: #0f172a; }
          .evidence-modal .evidence-customer-block {
            margin: 0 0 14px;
            padding: 10px 12px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }
          .evidence-modal .evidence-customer-row { display: flex; gap: 6px; align-items: flex-start; }
          .evidence-modal .evidence-customer-row + .evidence-customer-row { margin-top: 6px; }
          .evidence-modal .evidence-customer-label { flex-shrink: 0; font-size: 10px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; min-width: 66px; padding-top: 1px; }
          .evidence-modal .evidence-customer-value { font-size: 11.5px; font-weight: 500; color: #0f172a; line-height: 1.45; word-wrap: break-word; overflow-wrap: anywhere; }
          .evidence-modal .evidence-field-label { margin: 12px 0 4px; font-size: 11px; font-weight: 500; color: #475569; }
          .evidence-modal .evidence-select,
          .evidence-modal .evidence-textarea { font-size: 11px !important; border-radius: 6px !important; border: 1px solid #cbd5e1 !important; padding: 7px 10px !important; color: #0f172a; }
          .evidence-modal .evidence-textarea { min-height: 84px !important; resize: vertical; }
          .evidence-modal .evidence-select:focus,
          .evidence-modal .evidence-textarea:focus { border-color: #0e7490 !important; box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.1) !important; }
          .evidence-modal .evidence-counter { margin-top: 4px; font-size: 10px; text-align: right; color: #94a3b8; }
          .evidence-modal .evidence-counter.is-ok { color: #16a34a; }
          .evidence-modal .evidence-video-frame { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; background: #000; height: 260px; display: flex; align-items: center; justify-content: center; }
          .evidence-modal .evidence-video-empty { border: 1px dashed #cbd5e1; border-radius: 8px; background: #fff; height: 260px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 6px; color: #94a3b8; text-align: center; padding: 16px; }
          .evidence-modal .evidence-video-empty p { margin: 0; font-size: 11px; color: #64748b; }
          .evidence-modal .evidence-btn { border-radius: 6px !important; padding: 6px 14px !important; font-size: 12px !important; font-weight: 600 !important; min-width: 88px; display: inline-flex; align-items: center; justify-content: center; gap: 6px; }
          @media (max-width: 767.98px) {
            .evidence-modal .modal-dialog { margin: 8px; max-width: none; }
            .evidence-modal .modal-body { max-height: calc(100vh - 200px); }
            .evidence-modal .evidence-col-left { border-right: 0; border-bottom: 1px solid #e5e7eb; padding: 12px 14px; }
            .evidence-modal .evidence-col-right { padding: 12px 14px; }
            .evidence-modal .evidence-video-frame,
            .evidence-modal .evidence-video-empty { height: 200px; }
            .evidence-modal .evidence-title { font-size: 13px; }
            .evidence-modal .modal-footer { padding: 10px 12px; gap: 8px; }
            .evidence-modal .evidence-btn { flex: 1; min-width: 0; padding: 8px 10px !important; font-size: 11px !important; }
          }
        `}</style>

        <Modal.Header closeButton>
          <Modal.Title as="span" className="evidence-title">
            Check {evidenceOrder?.orderType === 1 ? 'Return' : evidenceOrder?.orderType === 2 ? 'Exchange' : ''} Evidence
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row g-0">
            <div className="col-md-6 evidence-col-left">
              {(evidenceReturnReason || evidenceReturnComments) && (
                <div className="evidence-customer-block">
                  {evidenceReturnReason && (
                    <div className="evidence-customer-row">
                      <span className="evidence-customer-label">Reason</span>
                      <span className="evidence-customer-value">{evidenceReturnReason}</span>
                    </div>
                  )}
                  {evidenceReturnComments && (
                    <div className="evidence-customer-row">
                      <span className="evidence-customer-label">Comment</span>
                      <span className="evidence-customer-value">{evidenceReturnComments}</span>
                    </div>
                  )}
                </div>
              )}
              <h6 className="evidence-section-label">Rejection Reason</h6>
              <Form.Group>
                <Form.Select
                  className="evidence-select"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  disabled={evidenceLoading}
                >
                  <option value="">Choose a rejection reason...</option>
                  {EVIDENCE_REJECTION_REASONS.map((reason, index) => (
                    <option key={index} value={reason}>{reason}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label className="evidence-field-label">Any other reason</Form.Label>
                <Form.Control
                  as="textarea"
                  className="evidence-textarea"
                  rows={4}
                  placeholder="Provide specific reason for rejection (minimum 10 characters)..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  disabled={evidenceLoading}
                />
                <div className={`evidence-counter${customReason && customReason.trim().length >= 10 ? ' is-ok' : ''}`}>
                  {customReason?.length || 0}/10 characters
                  {customReason && customReason.trim().length >= 10 && ' ✓'}
                </div>
              </Form.Group>
            </div>

            <div className="col-md-6 evidence-col-right">
              {evidenceVideoUrl ? (
                <div className="evidence-video-frame">
                  <video
                    key={evidenceVideoUrl}
                    controls
                    autoPlay
                    muted
                    playsInline
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  >
                    <source src={evidenceVideoUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div className="evidence-video-empty">
                  <FiImage size={30} />
                  <p>No evidence video available</p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="danger"
            className="evidence-btn"
            onClick={handleEvidenceReject}
            disabled={evidenceLoading || !(rejectionReason || (customReason && customReason.trim().length >= 10))}
          >
            {evidenceAction === 'REJECT' ? (<><Spinner as="span" animation="border" size="sm" /> Processing...</>) : 'Reject'}
          </Button>
          <Button
            variant="success"
            className="evidence-btn"
            onClick={handleEvidenceApprove}
            disabled={evidenceLoading || (rejectionReason || (customReason.trim().length > 0))}
          >
            {evidenceAction === 'APPROVE' ? (<><Spinner as="span" animation="border" size="sm" /> Processing...</>) : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .modern-dashboard { color: #0f172a; background: #f9fafb; min-height: calc(100vh - 70px); }
        .main-content:has(.modern-dashboard) { background: #f9fafb; }
        .page-content:has(.modern-dashboard) { background: #f9fafb; }

        /* Live Ongoing Orders — mobile card list (mirrors the Orders page) */
        .live-orders-mobile-list { display: none; }

        /* Empty state — used in both the DataTable and mobile card list */
        .live-orders-empty {
          width: 100%;
          min-height: 340px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px 20px;
          text-align: center;
          color: #64748b;
          background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
        }
        /* Loading state background matches the empty state — no jarring white */
        .live-orders-loading {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, #fafbfd 0%, #f5f7fa 100%);
        }
        /* react-data-table-component wraps progressComponent in a white div —
           strip its background so our .live-orders-loading gradient shows. */
        .live-orders-panel .rdt_TableBody,
        .live-orders-panel .rdt_Table > div[role="progressbar"],
        .live-orders-panel [data-testid="table"] > div:has(.live-orders-loading) {
          background: transparent !important;
        }
        .live-orders-empty-icon {
          font-size: 40px;
          margin-bottom: 12px;
          opacity: 0.7;
          filter: grayscale(0.2);
        }
        .live-orders-empty-title {
          font-size: 15px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.01em;
          margin-bottom: 4px;
        }
        .live-orders-empty-sub {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 500;
        }

        /* Make the whole panel + table area fill more vertical space */
        .modern-panel:has(.live-orders-table) { min-height: 480px; }
        .live-orders-table .MuiPaper-root { min-height: 380px; }
        .live-orders-table .rdt_TableCell,
        .live-orders-table .rdt_TableRow { min-height: 44px; }
        .live-order-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 12px;
          cursor: pointer;
          transition: transform 0.12s ease, border-color 0.15s ease;
          display: flex;
          flex-direction: column;
          gap: 10px;
          align-items: stretch;
        }
        .live-order-card:active { transform: scale(0.99); border-color: #008695; }
        .live-order-card-id { font-size: 10px; font-weight: 700; color: #0f172a; }
        .live-order-card-date { font-size: 9px; color: #64748b; margin-top: 1px; }
        .live-order-card-chip {
          font-size: 10px !important;
          font-weight: 600;
          padding: 3px 8px !important;
          border-radius: 4px;
          white-space: nowrap;
          letter-spacing: 0.01em;
        }
        .live-order-card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 6px;
          font-size: 10px;
        }
        .live-order-card-label { color: #64748b; font-size: 10px; }
        .live-order-card-amount { color: #008695; font-weight: 700; font-size: 11px; }
        .live-order-card-price {
          color: #008695;
          font-weight: 800;
          font-size: 15px;
          letter-spacing: -0.02em;
          line-height: 1.1;
          margin: 2px 0 4px;
        }
        .live-order-card-pay {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px;
          border-radius: 999px;
          background: #f8fafc;
          border: 1px solid #eef2f7;
          width: fit-content;
        }
        .live-order-card-pay-mode,
        .live-order-card-pay-status {
          font-size: 9px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 999px;
          white-space: nowrap;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .live-order-card-pay-status {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .live-order-card-pay-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          display: inline-block;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.5);
        }

        @media (max-width: 767.98px) {
          .live-orders-table { display: none !important; }
          .live-orders-mobile-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 8px 0 24px;
            min-height: 420px;
          }
          .live-orders-mobile-list .live-orders-empty {
            min-height: 380px;
            border-radius: 12px;
            border: 1px solid #eef2f7;
          }

          /* Live Ongoing Orders — strip the outer panel wrapper on mobile; cards inherit the same 8px gutter as the dashboard stat grid */
          .modern-panel.live-orders-panel {
            background: transparent !important;
            border: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          .modern-panel.live-orders-panel .modern-panel-header {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .modern-panel.live-orders-panel .modern-panel-body,
          .modern-panel.live-orders-panel > .products-and-options,
          .modern-panel.live-orders-panel .MuiPaper-root { padding: 0 !important; margin: 0 !important; background: transparent !important; }
          .modern-panel.live-orders-panel .live-orders-mobile-list {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .modern-panel.live-orders-panel .live-order-card {
            border-radius: 8px !important;
            border: 1px solid #e2e8f0 !important;
          }

          .modern-dashboard { padding-left: 0 !important; padding-right: 0 !important; }
          .modern-panel {
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          .modern-panel-header {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .modern-dashboard .filter_wrapper { padding-left: 0 !important; padding-right: 0 !important; }
          .modern-dashboard .row { margin-left: -4px !important; margin-right: -4px !important; }
          .modern-dashboard .row > [class*="col-"] { padding-left: 4px !important; padding-right: 4px !important; }
        }

        /* Zero page-content padding + trim 16px horizontal gutter so header can bleed edge-to-edge */
        body:has(.modern-dashboard) .page-content,
        body:has(.modern-dashboard) #layout-wrapper.no-topbar .page-content,
        body:has(.modern-dashboard) #layout-wrapper .page-content {
          padding-top: 0 !important;
          padding-bottom: 24px !important;
          padding-left: 16px !important;
          padding-right: 16px !important;
        }
        body:has(.modern-dashboard) .container-fluid,
        body:has(.modern-dashboard) .container {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        .dashboard-header {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .dashboard-greeting { min-width: 0; }
        .dashboard-greeting-text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .dashboard-greeting-sub {
          color: #94a3b8;
        }
        /* Sticky header — bleeds edge-to-edge; horizontal padding matches .modern-panel-header */
        .modern-dashboard .page-header-sticky {
          padding: clamp(14px, 1.6vw, 24px) clamp(14px, 1.4vw, 20px) clamp(10px, 1.2vw, 18px) !important;
          margin: 0 -16px 16px !important;
          top: 0 !important;
          background: #f9fafb !important;
          border-bottom: 1px solid #eef2f7 !important;
          box-shadow: none !important;
        }
        @media (max-width: 991px) {
          .modern-dashboard .page-header-sticky {
            padding: 16px 16px 12px !important;
          }
        }
        @media (max-width: 767.98px) {
          .modern-dashboard .page-header-sticky {
            padding: 10px 16px 8px !important;
            margin: 0 -16px 8px !important;
          }
          .dashboard-greeting-text {
            font-size: 13px !important;
            line-height: 1.2 !important;
            letter-spacing: -0.01em;
          }
          .dashboard-greeting p,
          .dashboard-greeting-sub {
            font-size: 11px !important;
            margin: 2px 0 0 !important;
            line-height: 1.3 !important;
          }
        }
        @media (max-width: 480px) {
          .modern-dashboard .page-header-sticky {
            padding: 10px 16px 8px !important;
            margin: 0 -16px 8px !important;
          }
          .dashboard-greeting-text { font-size: 12px !important; }
          .dashboard-greeting p,
          .dashboard-greeting-sub { font-size: 10.5px !important; }
        }

        @media (min-width: 992px) {
          .dashboard-header {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
          }
          .dashboard-greeting { flex: 0 0 auto; }
          .filters-row { flex: 1 1 auto; min-width: 0; width: auto; justify-content: flex-end; }
        }

        .filters-row {
          display: flex !important;
          flex-wrap: nowrap !important;
          align-items: center;
          gap: 8px;
          overflow: visible;
          padding: 4px 2px 6px;
          width: 100%;
        }
        .filters-row > * { flex-shrink: 0 !important; }
        .filters-to { white-space: nowrap; color: #94a3b8; padding: 0 2px; }
        @media (min-width: 768px) { .filters-row { gap: 12px; } }

        /* Mobile: single-row grid — start + end + refresh */
        @media (max-width: 767.98px) {
          .filters-row {
            display: grid !important;
            grid-template-columns: 1fr 1fr 40px;
            grid-template-areas: "startDate endDate refresh";
            grid-column-gap: 8px;
            grid-row-gap: 8px;
            overflow: visible !important;
            padding: 4px 0 6px;
            width: 100%;
          }
          .filters-row > * { flex-shrink: 1 !important; min-width: 0 !important; }
          .filters-row .modern-refresh {
            grid-area: refresh;
            width: 40px !important;
            min-width: 40px !important;
            padding: 0 !important;
            justify-content: center;
            gap: 0 !important;
          }
          .filters-row .modern-refresh span { display: none !important; }
          .filters-row > input[type="date"]:nth-of-type(1) {
            grid-area: startDate;
            width: 100% !important;
            font-size: 12px !important;
          }
          .filters-row > input[type="date"]:nth-of-type(2) {
            grid-area: endDate;
            width: 100% !important;
            font-size: 12px !important;
          }
          .filters-to { display: none !important; }
        }

        /* Custom dropdown (replaces native <select> so the open list can be styled) */
        .modern-dd {
          position: relative;
          display: inline-block;
        }
        .modern-dd-trigger {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 0 12px;
          background-color: #fff;
          color: #0f172a;
          font-size: 12px;
          font-weight: 500;
          min-width: 170px;
          height: 34px;
          cursor: pointer;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .modern-dd-trigger:hover { border-color: #cbd5e1; }
        .modern-dd-trigger:focus,
        .modern-dd-trigger.is-open {
          border-color: #008695;
          box-shadow: 0 0 0 3px rgba(0,134,149,0.15);
          outline: none;
        }
        .modern-dd-lead { color: #008695; flex-shrink: 0; }
        .modern-dd-label { flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .modern-dd-caret { transition: transform 0.18s ease; flex-shrink: 0; }
        .modern-dd-caret.is-open { transform: rotate(180deg); }

        .modern-dd-menu {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          z-index: 1100;
          min-width: 100%;
          width: max-content;
          max-height: 320px;
          overflow-y: auto;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.06);
          padding: 6px;
          animation: modernDdIn 0.14s ease-out;
        }
        @keyframes modernDdIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .modern-dd-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          background: transparent;
          border: none;
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 500;
          color: #0f172a;
          text-align: left;
          cursor: pointer;
          transition: background 0.12s ease, color 0.12s ease;
        }
        .modern-dd-item:hover { background: #f1f5f9; }
        .modern-dd-item.is-active {
          background: rgba(0,134,149,0.08);
          color: #008695;
          font-weight: 600;
        }
        .modern-dd-item-muted { color: #64748b; }
        .modern-dd-divider {
          height: 1px;
          background: #eef2f7;
          margin: 6px 4px;
        }

        /* Wrapper so we can overlay a dd/mm/yy formatted label on top of the native input */
        .modern-date-wrap {
          position: relative;
          display: inline-block;
        }
        .modern-date-display {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 500;
          color: #0f172a;
          letter-spacing: 0.01em;
          pointer-events: none;
          padding: 0 8px;
          white-space: nowrap;
        }
        .modern-date-wrap:has(.modern-date[value=""]) .modern-date-display,
        .modern-date-wrap .modern-date-display:empty { color: #94a3b8; }

        /* Hide the native rendered text — the overlay above shows the formatted value */
        .modern-date-wrap .modern-date {
          color: transparent !important;
          caret-color: transparent;
        }

        .modern-date {
          position: relative;
          display: inline-block !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          padding: 5px 8px !important;
          width: auto !important;
          min-width: 115px !important;
          height: 34px !important;
          background-color: #fff !important;
          color: #0f172a !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          text-align: center !important;
          margin: 0 !important;
          font-family: inherit;
          cursor: pointer !important;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .modern-date:hover { border-color: #cbd5e1 !important; }
        .modern-date:focus {
          border-color: #008695 !important;
          box-shadow: 0 0 0 3px rgba(0,134,149,0.15) !important;
          outline: none;
        }
        /* Hide native calendar icon; whole field is clickable to open picker */
        .modern-date::-webkit-calendar-picker-indicator {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          padding: 0;
          margin: 0;
          background: transparent;
          color: transparent;
          opacity: 0;
          cursor: pointer;
        }
        .modern-date::-webkit-inner-spin-button,
        .modern-date::-webkit-clear-button { display: none; }
        @media (max-width: 576px) {
          .modern-date { padding: 5px 10px !important; }
          .modern-dd-trigger { min-width: 150px; padding: 0 10px; }
        }

        .modern-refresh {
          border: 1px solid #e2e8f0 !important;
          background: #fff !important;
          color: #475569 !important;
          border-radius: 8px !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          height: 34px !important;
          white-space: nowrap;
          transition: all 0.15s ease;
        }
        .modern-refresh:hover:not(:disabled) {
          border-color: #008695 !important;
          color: #008695 !important;
          background: rgba(0,134,149,0.06) !important;
        }
        .modern-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

        .filters-to {
          color: #94a3b8;
          font-size: 11px;
          font-weight: 500;
        }

        .filters-clear {
          height: 34px !important;
          border-radius: 8px !important;
          white-space: nowrap;
          font-size: 12px !important;
        }


        .modern-stat-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: clamp(10px, 1vw, 16px);
          margin-top: clamp(12px, 1.4vw, 20px);
        }
        @media (max-width: 1200px) { .modern-stat-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 900px)  { .modern-stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; } }
        /* Mobile: 2 rows × 2 columns visible, remaining cards accessible via
           horizontal scroll. Cards flow top→bottom, then next column. */
        @media (max-width: 767.98px) {
          .modern-stat-grid {
            display: grid;
            grid-template-columns: unset;
            grid-auto-flow: column;
            grid-template-rows: repeat(2, auto);
            grid-auto-columns: calc(50% - 5px);
            gap: 10px;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            padding: 2px 2px 8px;
            margin-right: -12px;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .modern-stat-grid::-webkit-scrollbar { display: none; height: 0; }
          .modern-stat-grid > .modern-stat-card {
            scroll-snap-align: start;
            min-width: 0;
          }
        }
        @media (max-width: 767.98px) {
          .modern-stat-card > div:nth-child(2) {
            font-size: 20px !important;
          }
        }
        @media (max-width: 480px)  {
          .modern-stat-card { padding: 14px 12px; border-radius: 12px; }
          .stat-icon-wrap { width: 24px; height: 24px; border-radius: 6px; }
          .stat-top-row { margin-bottom: 14px; }
          .stat-foot { margin-top: 10px; padding-top: 8px; }
          .modern-stat-card > div:nth-child(2) {
            font-size: 18px !important;
          }
        }

        .modern-stat-card {
          background: #ffffff;
          border: 1px solid #f1f5f9;
          border-radius: 16px;
          padding: clamp(14px, 1.4vw, 22px);
          transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.15s ease;
          box-shadow: 0 1px 2px rgba(15,23,42,0.03);
          display: flex;
          flex-direction: column;
        }
        .modern-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 28px -18px rgba(15,23,42,0.14);
          border-color: #e2e8f0;
        }
        .modern-stat-card-clickable { cursor: pointer; }
        .modern-stat-card-clickable:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px rgba(0,134,149,0.25), 0 12px 28px -18px rgba(15,23,42,0.14);
          border-color: #008695;
        }
        .stat-top-row {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: clamp(14px, 1.4vw, 22px); gap: 10px;
        }
        .stat-icon-wrap {
          width: clamp(26px, 2vw, 32px);
          height: clamp(26px, 2vw, 32px);
          border-radius: 8px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .stat-foot {
          margin-top: clamp(10px, 0.9vw, 14px); padding-top: clamp(8px, 0.75vw, 12px);
          border-top: 1px solid #f1f5f9;
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px;
        }
        .stat-foot-cycle { cursor: pointer; user-select: none; position: relative; }
        .stat-foot-cycle:focus-visible { outline: none; }
        .stat-foot-progress {
          position: absolute;
          top: -1px; left: 0;
          height: 2px;
          width: 0;
          border-radius: 999px;
          animation-name: statFootFill;
          animation-timing-function: linear;
          animation-fill-mode: forwards;
          pointer-events: none;
        }
        @keyframes statFootFill {
          from { width: 0; }
          to   { width: 100%; }
        }
        /* Vertical scroll swap — used on the Category breakdown card.
           Two independent wrappers (one for label, one for value) so the
           parent .stat-foot's space-between layout is preserved. */
        .stat-foot-vscroll-wrap {
          display: inline-flex;
          align-items: baseline;
          overflow: hidden;
          line-height: 1.4;
        }
        .stat-foot-vscroll-inner {
          display: inline-block;
          animation: statFootVScroll 600ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: transform, opacity;
        }
        @keyframes statFootVScroll {
          0%   { opacity: 0; transform: translateY(100%); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .stat-foot-vscroll-inner { animation: none; }
        }

        .modern-panel {
          background: #fff;
          border: 1px solid #eef2f7;
          border-radius: 16px;
          box-shadow: 0 1px 2px rgba(15,23,42,0.03);
          overflow: hidden;
        }
        .modern-panel-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: clamp(12px, 1.2vw, 18px) clamp(14px, 1.4vw, 20px) clamp(8px, 0.9vw, 12px);
          gap: 12px; flex-wrap: wrap;
        }
        .modern-panel-body { padding: 8px clamp(14px, 1.4vw, 20px) clamp(14px, 1.4vw, 20px); }

        .modern-tabs { border-bottom: 1px solid #eef2f7; gap: 4px; }
        .modern-tab {
          color: #64748b !important;
          padding: 7px 14px !important;
          border: none !important;
          border-bottom: 2px solid transparent !important;
          background: transparent !important;
          transition: all 0.15s ease;
        }
        .modern-tab:hover { color: #0f172a !important; background: #f8fafc !important; }
        .modern-tab.active {
          color: #4f46e5 !important;
          border-bottom-color: #4f46e5 !important;
          background: transparent !important;
        }

        .modern-order-pill {
          background: #eef2ff; color: #4338ca;
          padding: 3px 8px; border-radius: 6px;
          font-weight: 600; font-size: 11px;
          cursor: pointer; min-width: 72px; text-align: center;
          transition: all 0.15s ease;
        }
        .modern-order-pill:hover { background: #e0e7ff; }

        .status-chip {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 3px 8px; border-radius: 999px;
          font-size: 11px; font-weight: 600;
        }
        .status-dot { width: 5px; height: 5px; border-radius: 50%; display: inline-block; }

        .linked-id {
          text-decoration: none; cursor: pointer; font-weight: 600; font-size: 11px;
          padding: 2px 7px; border-radius: 5px; transition: all 0.15s ease;
        }
        .linked-id.return-id { color: #dc2626; background: #fef2f2; }
        .linked-id.return-id:hover { background: #fee2e2; }
        .linked-id.exchange-id { color: #0369a1; background: #f0f9ff; }
        .linked-id.exchange-id:hover { background: #e0f2fe; }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* ===== Mobile: inline greeting icons + date bottom sheet ===== */
        .dashboard-greeting-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .dashboard-greeting-text-block {
          min-width: 0;
          flex: 1 1 auto;
        }
        .dashboard-greeting-text-block > p {
          margin-top: 2px !important;
        }
        .dashboard-greeting-actions { display: none; }

        @media (max-width: 767.98px) {
          /* Hide the desktop filters-row on mobile — icons take over */
          .modern-dashboard .filters-row { display: none !important; }

          .dashboard-greeting-actions {
            display: inline-flex;
            gap: 6px;
            flex-shrink: 0;
          }
          .dgi-btn {
            width: 34px; height: 34px;
            display: inline-flex; align-items: center; justify-content: center;
            border: 1px solid #e2e8f0;
            background: #fff;
            color: #475569;
            border-radius: 8px;
            cursor: pointer;
            transition: all 0.15s ease;
            padding: 0;
          }
          .dgi-btn:hover:not(:disabled) {
            border-color: #008695;
            color: #008695;
            background: rgba(0,134,149,0.06);
          }
          .dgi-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        }

        /* ===== Date range bottom sheet ===== */
        @keyframes ddsSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes ddsFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .dds-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(15,23,42,0.35);
          z-index: 1099;
          animation: ddsFadeIn 0.18s ease-out;
        }
        .dds-sheet {
          position: fixed;
          left: 0; right: 0; bottom: 0;
          background: #fff;
          border-radius: 18px 18px 0 0;
          box-shadow: 0 -8px 24px rgba(15,23,42,0.15);
          z-index: 1100;
          display: flex;
          flex-direction: column;
          max-height: 88vh;
          animation: ddsSlideUp 0.24s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .dds-handle {
          width: 42px; height: 4px;
          background: #cbd5e1;
          border-radius: 999px;
          margin: 8px auto 0;
        }
        .dds-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px 8px;
        }
        .dds-title {
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.01em;
        }
        .dds-close {
          width: 28px; height: 28px;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background: #fff;
          color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .dds-close:hover { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
        .dds-body {
          padding: 4px 16px 8px;
          overflow-y: auto;
        }
        .dds-presets {
          display: flex;
          flex-wrap: nowrap;
          gap: 6px;
          padding: 4px 0 12px;
          border-bottom: 1px solid #eef2f7;
          margin-bottom: 10px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          scroll-snap-type: x proximity;
        }
        .dds-presets::-webkit-scrollbar { display: none; }
        .dds-preset {
          padding: 6px 12px;
          font-size: 11px;
          font-weight: 600;
          color: #334155;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          cursor: pointer;
          transition: all 0.15s ease;
          flex-shrink: 0;
          white-space: nowrap;
          scroll-snap-align: start;
        }
        .dds-preset:hover {
          background: #fff;
          border-color: #008695;
          color: #008695;
        }
        .dds-field { margin-bottom: 10px; }
        .dds-label {
          display: block;
          font-size: 10px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          margin-bottom: 4px;
        }
        .dds-input {
          width: 100%;
          height: 40px;
          font-size: 13px;
          padding: 6px 12px;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #0f172a;
          font-family: inherit;
        }
        /* Overlay dd-MMM-yy formatted label on top of the native date input */
        .dds-date-wrap {
          position: relative;
          display: block;
        }
        .dds-date-wrap .dds-input {
          color: transparent !important;
          caret-color: transparent;
        }
        .dds-date-wrap .dds-input::-webkit-calendar-picker-indicator {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          padding: 0;
          margin: 0;
          background: transparent;
          color: transparent;
          opacity: 0;
          cursor: pointer;
        }
        .dds-date-display {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          padding: 0 12px;
          font-size: 13px;
          font-weight: 500;
          color: #0f172a;
          letter-spacing: 0.01em;
          pointer-events: none;
          white-space: nowrap;
        }
        .dds-input:focus {
          outline: none;
          border-color: #008695;
          box-shadow: 0 0 0 3px rgba(0,134,149,0.15);
        }
        .dds-footer {
          display: flex;
          gap: 10px;
          padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
          border-top: 1px solid #eef2f7;
          background: #fff;
        }
        .dds-btn {
          flex: 1;
          height: 42px;
          font-size: 13px;
          font-weight: 700;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .dds-btn-secondary {
          background: #fff;
          color: #0f172a;
          border: 1px solid #e2e8f0;
        }
        .dds-btn-secondary:hover { border-color: #cbd5e1; background: #f8fafc; }
        .dds-btn-primary {
          background: #008695;
          color: #fff;
        }
        .dds-btn-primary:hover { background: #006672; }
      `}</style>

      {/* Mobile date range bottom sheet */}
      {showDateSheet && (
        <>
          <div
            className="dds-backdrop"
            onClick={() => setShowDateSheet(false)}
            aria-hidden="true"
          />
          <div className="dds-sheet" role="dialog" aria-label="Select date range">
            <div className="dds-handle" aria-hidden="true" />
            <div className="dds-header">
              <span className="dds-title">Select date range</span>
              <button
                type="button"
                className="dds-close"
                onClick={() => setShowDateSheet(false)}
                aria-label="Close"
              >
                <FiX size={16} />
              </button>
            </div>

            <div className="dds-body">
              <div className="dds-presets">
                {HOMEPAGE_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className="dds-preset"
                    onClick={() => {
                      const { start, end } = computePresetRange(p);
                      setSheetStart(start);
                      setSheetEnd(end);
                      setGlobalFilter(prev => ({ ...prev, startDate: start, endDate: end, timeRange: '4' }));
                      applyGlobalFilter('4', start, end);
                      setShowDateSheet(false);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="dds-field">
                <label className="dds-label">Start Date</label>
                <span className="dds-date-wrap">
                  <input
                    type="date"
                    className="dds-input"
                    value={sheetStart}
                    max={sheetEnd || undefined}
                    onChange={(e) => setSheetStart(e.target.value)}
                  />
                  <span className="dds-date-display" aria-hidden="true">{formatDdMmYy(sheetStart) || 'Select date'}</span>
                </span>
              </div>
              <div className="dds-field">
                <label className="dds-label">End Date</label>
                <span className="dds-date-wrap">
                  <input
                    type="date"
                    className="dds-input"
                    value={sheetEnd}
                    min={sheetStart || undefined}
                    onChange={(e) => setSheetEnd(e.target.value)}
                  />
                  <span className="dds-date-display" aria-hidden="true">{formatDdMmYy(sheetEnd) || 'Select date'}</span>
                </span>
              </div>
            </div>

            <div className="dds-footer">
              <button
                type="button"
                className="dds-btn dds-btn-secondary"
                onClick={() => setShowDateSheet(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dds-btn dds-btn-primary"
                onClick={applyDateSheet}
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
